// Type definitions for design tokens
export type TokenScale = Record<string, string>;
export type NestedTokenScale = Record<string, TokenScale | string>;
export type ResponsiveScale = Record<string, TokenScale>;
export type TypographyPattern = Record<string, TokenScale>;
export type LayoutToken = Record<string, unknown>;

const GAP_SCALE: TokenScale = {
  none: 'gap-0',
  0.5: 'gap-0.5',
  1: 'gap-1',
  1.5: 'gap-1.5',
  2: 'gap-2',
  2.5: 'gap-2.5',
  3: 'gap-3',
  3.5: 'gap-3.5',
  4: 'gap-4',
  5: 'gap-5',
  6: 'gap-6',
  7: 'gap-7',
  8: 'gap-8',
  9: 'gap-9',
  10: 'gap-10',
  11: 'gap-11',
  12: 'gap-12',
  13: 'gap-13',
  14: 'gap-14',
  15: 'gap-15',
  16: 'gap-16',
  17: 'gap-17',
  18: 'gap-18',
  19: 'gap-19',
  20: 'gap-20',
  21: 'gap-21',
  22: 'gap-22',
  23: 'gap-23',
  24: 'gap-24',
  25: 'gap-25',
  26: 'gap-26',
  27: 'gap-27',
  28: 'gap-28',
  29: 'gap-29',
  30: 'gap-30',
  31: 'gap-31',
  32: 'gap-32',
  // Semantic aliases
  xs: 'gap-1',
  sm: 'gap-2',
  md: 'gap-4',
  lg: 'gap-6',
  xl: 'gap-8',
  '2xl': 'gap-12',
  '3xl': 'gap-16',
  '4xl': 'gap-20',
  '5xl': 'gap-24',
};

// Responsive gap utilities
const RESPONSIVE_GAP_SCALE = {
  y: {
    6: 'gap-y-6',
    sm: {
      6: 'sm:gap-y-6',
    },
    md: {
      6: 'md:gap-y-6',
    },
  },
  x: {
    lg: {
      6: 'lg:gap-x-6',
    },
    xl: {
      6: 'xl:gap-x-6',
    },
  },
};

// Gap X and Y specific scales
const GAP_X_SCALE = {
  0: 'gap-x-0',
  0.5: 'gap-x-0.5',
  1: 'gap-x-1',
  1.5: 'gap-x-1.5',
  2: 'gap-x-2',
  2.5: 'gap-x-2.5',
  3: 'gap-x-3',
  3.5: 'gap-x-3.5',
  4: 'gap-x-4',
  5: 'gap-x-5',
  6: 'gap-x-6',
  7: 'gap-x-7',
  8: 'gap-x-8',
};

const GAP_Y_SCALE = {
  0: 'gap-y-0',
  0.5: 'gap-y-0.5',
  1: 'gap-y-1',
  1.5: 'gap-y-1.5',
  2: 'gap-y-2',
  2.5: 'gap-y-2.5',
  3: 'gap-y-3',
  3.5: 'gap-y-3.5',
  4: 'gap-y-4',
  5: 'gap-y-5',
  6: 'gap-y-6',
  7: 'gap-y-7',
  8: 'gap-y-8',
};

const OVERFLOW_Y = {
  auto: 'overflow-y-auto',
  hidden: 'overflow-y-hidden',
  scroll: 'overflow-y-scroll',
  visible: 'overflow-y-visible',
};

const OVERFLOW = {
  'y-auto': 'overflow-y-auto',
  'y-hidden': 'overflow-y-hidden',
  'y-scroll': 'overflow-y-scroll',
  'y-visible': 'overflow-y-visible',
  'x-auto': 'overflow-x-auto',
  'x-hidden': 'overflow-x-hidden',
  'x-scroll': 'overflow-x-scroll',
  'x-visible': 'overflow-x-visible',
  auto: 'overflow-auto',
  hidden: 'overflow-hidden',
  scroll: 'overflow-scroll',
  visible: 'overflow-visible',
  'x-auto-hide': 'overflow-x-auto scrollbar-hide',
  scrollbarThin: 'scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100',
};

const SPACING = {
  spaceX2: 'space-x-2',
  spaceY2: 'space-y-2',
  spaceY3: 'space-y-3',
  spaceY4: 'space-y-4',
  spaceY6: 'space-y-6',
};

const INTERACTION = {
  cursorPointer: 'cursor-pointer',
};

const FLEX_SHRINK = {
  0: 'shrink-0',
  1: 'shrink',
};

const FLEX_GROW = {
  0: 'grow-0',
  1: 'grow',
};

const FLEX_BASIS = {
  full: 'basis-full',
  auto: 'basis-auto',
};

const FLEX_WRAP = {
  wrap: 'flex-wrap',
  nowrap: 'flex-nowrap',
  'wrap-reverse': 'flex-wrap-reverse',
};

const FLEX_SELF = {
  center: 'self-center',
  start: 'self-start',
  end: 'self-end',
  stretch: 'self-stretch',
};

const FLEX_ALIGN_SCALE = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  stretch: 'items-stretch',
  baseline: 'items-baseline',
};

const FLEX_JUSTIFY_SCALE = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
  between: 'justify-between',
  around: 'justify-around',
  evenly: 'justify-evenly',
};

// Interactive element heights - defined separately to avoid circular references
const INTERACTIVE_HEIGHTS = {
  touchTarget: 'h-11', // Minimum 44px for touch accessibility
  compact: 'h-8', // Small buttons, badges
  default: 'h-10', // Standard buttons, inputs
  comfortable: 'h-12', // Large buttons, emphasized inputs
  spacious: 'h-14', // Hero buttons, search bars
  generous: 'h-16', // Very large CTAs
};

const CONTROL_SIZES = {
  button: {
    sm: `${INTERACTIVE_HEIGHTS.compact} px-4 py-2 text-sm`,
    md: `${INTERACTIVE_HEIGHTS.default} px-6 py-3 text-base`,
    lg: `${INTERACTIVE_HEIGHTS.comfortable} px-7 py-3.5 text-base`,
    xl: `${INTERACTIVE_HEIGHTS.generous} px-8 py-4 text-lg`,
    icon: `${INTERACTIVE_HEIGHTS.comfortable} w-12 p-0`,
    default: `${INTERACTIVE_HEIGHTS.default} px-6 py-3 text-base`,
  },
  field: {
    sm: `${INTERACTIVE_HEIGHTS.touchTarget} px-3.5 py-2 text-sm`, // Ensure 44px minimum height
    md: `${INTERACTIVE_HEIGHTS.touchTarget} px-4 py-2.5 text-sm`, // Ensure 44px minimum height
    lg: `${INTERACTIVE_HEIGHTS.comfortable} px-5 py-4 text-base`,
  },
};

// Size scale for heights and widths
const SIZE_SCALE: TokenScale = {
  // Heights
  0: 'h-0',
  0.5: 'h-0.5',
  1: 'h-1',
  1.5: 'h-1.5',
  2: 'h-2',
  2.5: 'h-2.5',
  3: 'h-3',
  3.5: 'h-3.5',
  4: 'h-4',
  5: 'h-5',
  6: 'h-6',
  7: 'h-7',
  8: 'h-8',
  9: 'h-9',
  h4: 'h-4',
  h8: 'h-8',
  h9: 'h-9',
  h12: 'h-12',
  h14: 'h-14',
  10: 'h-10',
  11: 'h-11',
  12: 'h-12',
  13: 'h-13',
  14: 'h-14',
  15: 'h-15',
  16: 'h-16',
  18: 'h-18',
  20: 'h-20',
  24: 'h-24',
  28: 'h-28',
  32: 'h-32',
  35: 'h-35',
  36: 'h-36',
  40: 'h-40',
  44: 'h-44',
  48: 'h-48',
  52: 'h-52',
  56: 'h-56',
  60: 'h-60',
  64: 'h-64',
  72: 'h-72',
  80: 'h-80',
  88: 'h-88',
  96: 'h-96',
  120: 'h-120',
  160: 'h-160',

  // Widths
  w0: 'w-0',
  w1: 'w-1',
  'w1.5': 'w-1.5',
  w2: 'w-2',
  'w2.5': 'w-2.5',
  w3: 'w-3',
  'w3.5': 'w-3.5',
  w4: 'w-4',
  w5: 'w-5',
  w6: 'w-6',
  w7: 'w-7',
  w8: 'w-8',
  w9: 'w-9',
  w10: 'w-10',
  w11: 'w-11',
  w12: 'w-12',
  w13: 'w-13',
  w14: 'w-14',
  w15: 'w-15',
  w16: 'w-16',
  w18: 'w-18',
  w20: 'w-20',
  w24: 'w-24',
  w28: 'w-28',
  w32: 'w-32',
  w35: 'w-35',
  w36: 'w-36',
  w40: 'w-40',
  w44: 'w-44',
  w48: 'w-48',
  w52: 'w-52',
  w56: 'w-56',
  w60: 'w-60',
  w64: 'w-64',
  w72: 'w-72',
  w80: 'w-80',
  w88: 'w-88',
  w96: 'w-96',
  w120: 'w-120',
  w160: 'w-160',

  // Full width
  full: 'w-full',

  // Full height
  fullHeight: 'h-full',

  // Full size (width and height)
  fullSize: 'w-full h-full',

  // Auto
  auto: 'w-auto',

  // Fractional widths
  w1_2: 'w-1/2',
  w1_3: 'w-1/3',
  w2_3: 'w-2/3',
  w1_4: 'w-1/4',
  w3_4: 'w-3/4',
  w1_5: 'w-1/5',
  w2_5: 'w-2/5',
  w3_5: 'w-3/5',
  w4_5: 'w-4/5',

  // Special width utilities
  fit: 'w-fit',

  // CSS custom property widths
  componentWidthXl: 'w-[var(--component-width-xl)]',
  componentWidthMd: 'w-[var(--component-width-md)]',
  componentWidth2xl: 'w-[var(--component-width-2xl)]',
  componentWidthSm: 'w-[var(--component-width-sm)]',
  componentWidth3xl: 'w-[var(--component-width-3xl)]',
  componentWidth4xl: 'w-[var(--component-width-4xl)]',
  componentWidth5xl: 'w-[var(--component-width-5xl)]',
  componentWidth7xl: 'w-[var(--component-width-7xl)]',
  componentWidth8xl: 'w-[var(--component-width-8xl)]',

  // Min height screen
  screen: 'min-h-screen',

  // Full screen dimensions
  screenWidth: 'w-screen',
  screenHeight: 'h-screen',

  // Custom viewport-based sizes
  maxW90vw: 'max-w-[90vw]',
  maxH90vh: 'max-h-[90vh]',
  maxH80vh: 'max-h-[80vh]',

  // Custom calculated viewport heights
  maxHCalc30vh60px: 'max-h-[calc(30vh-60px)]',
  maxHCalc30vh80px: 'max-h-[calc(30vh-80px)]',
  maxHCalc30vh100px: 'max-h-[calc(30vh-100px)]',

  // Direct viewport height utilities
  maxH85vh: 'max-h-[85vh]',

  // Minimum widths (for specific UI elements)
  minW0: 'min-w-0',
  minW20: 'min-w-[20px]',
  minW40: 'min-w-[2.5rem]',
  minW52: 'min-w-[3.25rem]',
  minW56: 'min-w-[3.5rem]',
  minW60: 'min-w-[3.75rem]',
  minW64: 'min-w-[4rem]',
  minW72: 'min-w-[4.5rem]',
  minW80: 'min-w-[5rem]',
  minW96: 'min-w-[6rem]',
};

const RESPONSIVE_SIZE_SCALE = {
  maxH: {
    sm: {
      calc30vh60px: 'sm:max-h-[calc(30vh-60px)]',
      calc30vh80px: 'sm:max-h-[calc(30vh-80px)]',
      '70vh': 'sm:max-h-[70vh]',
      '90vh': 'sm:max-h-[90vh]',
    },
    md: {
      calc30vh100px: 'md:max-h-[calc(30vh-100px)]',
      '70vh': 'md:max-h-[70vh]',
      '90vh': 'md:max-h-[90vh]',
    },
    lg: {
      '700px': 'lg:max-h-[700px]',
    },
  },
  maxW: {
    sm: {
      md: 'sm:max-w-md',
      '2xl': 'sm:max-w-2xl',
      componentWidth4xl: 'sm:max-w-[var(--component-width-4xl)]',
    },
    lg: {
      md: 'lg:max-w-md',
      '3xl': 'lg:max-w-3xl',
    },
  },
  w: {
    sm: {
      auto: 'sm:w-auto',
    },
  },
  justify: {
    sm: {
      start: 'sm:justify-start',
      between: 'sm:justify-between',
    },
    lg: {
      start: 'lg:justify-start',
    },
  },
  items: {
    sm: {
      start: 'sm:items-start',
    },
  },
  flex: {
    sm: {
      row: 'sm:flex-row',
    },
    lg: {
      col: 'lg:flex-col',
    },
  },
  flex1: {
    sm: 'sm:flex-1',
    lg: 'lg:flex-1',
  },
  minH: {
    lg: {
      '700px': 'lg:min-h-[700px]',
    },
  },
  h: {
    lg: {
      '700px': 'lg:h-[700px]',
    },
  },
  bottom: {
    sm: {
      '-4': 'sm:-bottom-4',
    },
  },
  top: {
    sm: {
      3: 'sm:top-3',
    },
  },
  left: {
    sm: {
      3: 'sm:left-3',
    },
  },
  right: {
    sm: {
      3: 'sm:right-3',
    },
  },
  gridCols: {
    md: {
      2: 'md:grid-cols-2',
    },
  },
  colSpan: {
    full: 'col-span-full',
    md: {
      1: 'md:col-span-1',
    },
  },
};

const RESPONSIVE_TYPE_SCALE = {
  sm: {
    lg: 'sm:text-lg',
    xl: 'sm:text-xl',
  },
  lg: {
    xl: 'lg:text-xl',
  },
};

const MIN_WIDTH_SCALE = {
  // Minimum widths (for specific UI elements)
  minW0: 'min-w-0',
  minW20: 'min-w-[20px]',
  minW40: 'min-w-[2.5rem]',
  minW52: 'min-w-[3.25rem]',
  minW44: 'min-w-[2.75rem]',
  minW100: 'min-w-[100px]',
  minW200: 'min-w-[200px]',
  w130: 'w-[130px]',
  w180: 'w-[180px]',
  w112: 'w-112',
  minW170: 'min-w-[170px]',
  minW56: 'min-w-[56px]',
  h300: 'h-[300px]',
  w300: 'w-[300px]',
  minW36: 'min-w-[2.25rem]',
  minW80: 'min-w-[80px]',
  minW280: 'min-w-[280px]',
  w240: 'w-[240px]',
  w320: 'w-[320px]',
  w2px: 'w-[2px]',
  w70: 'w-[70px]',
  w160px: 'w-[160px]',
  w150px: 'w-[150px]',
  w140px: 'w-[140px]',
  w120px: 'w-[120px]',
  minW64: 'min-w-[4rem]',
  minW210: 'min-w-[210px]',
  minW40px: 'min-w-[40px]',
  minW24: 'min-w-[24px]',
  w260: 'w-[260px]',
  minW288: 'min-w-[18rem]',
  minW256: 'min-w-[16rem]',
  minW48: 'min-w-[3rem]',
  h64: 'h-64',
  h80: 'h-80',
  h72: 'h-[72px]',
  w72px: 'w-[72px]',
  minH24: 'min-h-[24px]',
  minH32: 'min-h-[32px]',
  minH40: 'min-h-[40px]',
  minH44: 'min-h-[44px]',
  minH48: 'min-h-[48px]',
  minH52: 'min-h-[52px]',
  minH56: 'min-h-[56px]',
  minH60: 'min-h-[60px]',
  minH140: 'min-h-[140px]',
  minH180: 'min-h-[180px]',
  minW60: 'min-w-[60px]',
  minW70: 'min-w-[70px]',
  minW128: 'min-w-[8rem]',
  minW160: 'min-w-[160px]',
  minW220: 'min-w-[220px]',
  cardResponsive: 'min-w-[var(--component-width-3xl,220px)] flex-shrink-0 snap-start md:min-w-0',
  h320: 'h-[320px]',

  // Fractional heights and widths
  h35: 'h-3.5',
  w35: 'w-3.5',

  // Additional commonly used sizes
  w40Percent: 'w-[40%]',
  h40Percent: 'h-[40%]',
  w60Percent: 'w-[60%]',
  w70Percent: 'w-[70%]',
  w65: 'w-[65%]',
  h16: 'h-16',
  h40: 'h-40',
  h52: 'h-52',
  h1_5: 'h-1.5',
  h4: 'h-4',
  h5: 'h-5',
  h7: 'h-7',
  h8: 'h-8',
  h10: 'h-10',
  h12: 'h-12',
  w360: 'w-[360px]',
  maxW4xl: 'max-w-4xl',
  maxW2xl: 'max-w-2xl',
  maxW7xl: 'max-w-7xl',
  maxW560: 'max-w-[560px]',
  maxW80: 'max-w-[80px]',
  maxW120px: 'max-w-[120px]',
  maxW150px: 'max-w-[150px]',
  maxWxs: 'max-w-xs',
  maxWFull: 'max-w-full',
  maxW150: 'max-w-[150px]',
  maxW240: 'max-w-[240px]',
  maxW320: 'max-w-[320px]',

  // Max heights
  maxH32: 'max-h-32',
  maxH48: 'max-h-48',
  maxH64: 'max-h-64',
  maxH120: 'max-h-[120px]',
  maxH360: 'max-h-[360px]',
  maxH400: 'max-h-[400px]',
  componentMaxHeightMd: 'max-h-[var(--component-max-height-md)]',
  componentMaxHeightLg: 'max-h-[var(--component-max-height-lg)]',

  // Icon sizes - commonly used dimensions for icons
  icon2Xs: 'w-2.5 h-2.5',
  iconXs: 'w-3.5 h-3.5',
  iconSm: 'w-4 h-4',
  iconMd: 'w-5 h-5',
  iconLg: 'w-6 h-6',
  iconXl: 'w-8 h-8',
  iconButton: 'w-4 h-4', // Standard size for button icons

  // Positioning helpers
  left0: 'left-0',
  right0: 'right-0',
  hpx: 'h-px',

  // Custom widths for specific UI patterns
  toastWidth: 'w-[calc(100vw-2rem)]',
};

// Max width scale for maximum width constraints
const MAX_WIDTH_SCALE = {
  // Standard Tailwind max-widths
  xs: 'max-w-xs',
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  '7xl': 'max-w-7xl',
  full: 'max-w-full',
  none: 'max-w-none',
  screen: 'max-w-screen',

  // Custom component widths
  componentWidthXl: 'max-w-[var(--component-width-xl)]',
  componentWidthMd: 'max-w-[var(--component-width-md)]',
  componentWidth2xl: 'max-w-[var(--component-width-2xl)]',
  componentWidthSm: 'max-w-[var(--component-width-sm)]',
  componentWidth3xl: 'max-w-[var(--component-width-3xl)]',
  componentWidth4xl: 'max-w-[var(--component-width-4xl)]',
  componentWidth5xl: 'max-w-[var(--component-width-5xl)]',
  componentWidth7xl: 'max-w-[var(--component-width-7xl)]',
  componentWidth8xl: 'max-w-[var(--component-width-8xl)]',
};

// Individual component height tokens for direct import
const componentMaxHeightMd = 'max-h-[var(--component-max-height-md)]';
const componentMaxHeightLg = 'max-h-[var(--component-max-height-lg)]';

const CONTROL_ICON_SIZES = {
  sm: '[&_svg]:size-4',
  md: '[&_svg]:size-[18px]',
  lg: '[&_svg]:size-5',
  xl: '[&_svg]:size-5',
  icon: '[&_svg]:size-5',
};

// Border radius scale
const RADIUS_SCALE = {
  none: 'rounded-none',
  sm: 'rounded-sm',
  md: 'rounded-md',
  lg: 'rounded-lg',
  xl: 'rounded-xl',
  '2xl': 'rounded-2xl',
  '3xl': 'rounded-3xl',
  pill: 'rounded-[999px]',
  full: 'rounded-full',
  responsive: 'rounded-xl sm:rounded-2xl',
};

// Shadow scale
const SHADOW_SCALE = {
  none: 'shadow-none',
  sm: 'shadow-sm',
  md: 'shadow-md',
  lg: 'shadow-lg',
  xl: 'shadow-xl',
  '2xl': 'shadow-2xl',
  inner: 'shadow-inner',
  // Additional commonly used shadow values
  glow: 'shadow-glow-sm',
  glowMd: 'shadow-glow-md',
  glowLg: 'shadow-glow-lg',
  glass: 'shadow-glass',
  glassLg: 'shadow-glass-lg',
  glassInset: 'shadow-glass-inset',
};

// Border width scale
const BORDER_WIDTH = {
  none: 'border-0',
  thin: 'border',
  0.5: 'border-[0.5px]',
  1: 'border',
  2: 'border-2',
};

const BORDER_STYLE = {
  solid: 'border-solid',
  dashed: 'border-dashed',
  dotted: 'border-dotted',
  double: 'border-double',
  none: 'border-none',
};

const BORDER_TOP = {
  0: 'border-t-0',
  thin: 'border-t',
  2: 'border-t-2',
  4: 'border-t-4',
  8: 'border-t-8',
};

const BORDER_LEFT = {
  thin: 'border-l',
};

const _BORDER_BOTTOM = {
  default: 'border-b',
  2: 'border-b-2',
};

const BORDER_COLOR = {
  black: {
    10: 'border-black/10',
  },
};

const BORDER = {
  default: 'border border-input',
  none: 'border-none',
  thin: 'border',
  medium: 'border-2',
};

const BACKGROUND_COLOR = {
  black: {
    0: 'bg-black/0',
    20: 'bg-black/20',
    30: 'bg-black/30',
    40: 'bg-black/40',
    60: 'bg-black/60',
  },
};

const OPACITY = {
  0: 'opacity-0',
  25: 'opacity-25',
  40: 'opacity-40',
  70: 'opacity-70',
  75: 'opacity-75',
  90: 'opacity-90',
  100: 'opacity-100',
};

const HOVER_OPACITY = {
  40: 'hover:opacity-40',
  80: 'hover:opacity-80',
  100: 'hover:opacity-100',
};

// Divide/border separator utilities
const DIVIDE = {
  y: {
    thin: 'divide-y',
    none: 'divide-y-0',
  },
  x: {
    thin: 'divide-x',
    none: 'divide-x-0',
  },
  color: {
    gray: {
      100: 'divide-gray-100',
      200: 'divide-gray-200',
      300: 'divide-gray-300',
    },
  },
};

// Positioning scale for absolute/fixed positioning
const POSITION_SCALE = {
  right: {
    0: 'right-0',
    1: 'right-1',
    2: 'right-2',
    3: 'right-3',
    4: 'right-4',
    6: 'right-6',
    8: 'right-8',
  },
  left: {
    0: 'left-0',
    1: 'left-1',
    2: 'left-2',
    3: 'left-3',
    4: 'left-4',
    6: 'left-6',
    8: 'left-8',
  },
  '-right': {
    2: '-right-2',
    3: '-right-3',
    4: '-right-4',
  },
  '-left': {
    2: '-left-2',
  },
  top: {
    0: 'top-0',
    1: 'top-1',
    2: 'top-2',
    3: 'top-3',
    4: 'top-4',
    6: 'top-6',
    8: 'top-8',
    16: 'top-16',
    20: 'top-20',
    24: 'top-24',
    '1/2': 'top-1/2',
  },
  '-top': {
    2: '-top-2',
  },
  bottom: {
    0: 'bottom-0',
    1: 'bottom-1',
    2: 'bottom-2',
    3: 'bottom-3',
    4: 'bottom-4',
    5: 'bottom-5',
    6: 'bottom-6',
    8: 'bottom-8',
    16: 'bottom-16',
    20: 'bottom-20',
  },
  '-bottom': {
    3: '-bottom-3',
    4: '-bottom-4',
  },
  // Positioning utilities
  fixed: 'fixed',
  absolute: 'absolute',
  relative: 'relative',
  static: 'static',
  sticky: 'sticky',
  inset: {
    0: 'inset-0',
  },
  insetX: {
    0: 'inset-x-0',
    6: 'inset-x-6',
    8: 'inset-x-8',
    10: 'inset-x-10',
  },
};

// Space scale for space-x and space-y utilities
const SPACE_SCALE = {
  0: 'space-x-0 space-y-0',
  0.5: 'space-x-0.5 space-y-0.5',
  1: 'space-x-1 space-y-1',
  1.5: 'space-x-1.5 space-y-1.5',
  2: 'space-x-2 space-y-2',
  2.5: 'space-x-2.5 space-y-2.5',
  3: 'space-x-3 space-y-3',
  4: 'space-x-4 space-y-4',
  5: 'space-x-5 space-y-5',
  6: 'space-x-6 space-y-6',
  8: 'space-x-8 space-y-8',
  10: 'space-x-10 space-y-10',
  12: 'space-x-12 space-y-12',
  16: 'space-x-16 space-y-16',
  20: 'space-x-20 space-y-20',
  24: 'space-x-24 space-y-24',
  32: 'space-x-32 space-y-32',
  // Semantic sizes
  xs: 'space-x-1 space-y-1',
  sm: 'space-x-2 space-y-2',
  md: 'space-x-4 space-y-4',
  lg: 'space-x-6 space-y-6',
  xl: 'space-x-8 space-y-8',
};

// Space X (horizontal only)
const SPACE_X_SCALE = {
  0: 'space-x-0',
  0.5: 'space-x-0.5',
  1: 'space-x-1',
  1.5: 'space-x-1.5',
  2: 'space-x-2',
  2.5: 'space-x-2.5',
  3: 'space-x-3',
  4: 'space-x-4',
  5: 'space-x-5',
  6: 'space-x-6',
  8: 'space-x-8',
  10: 'space-x-10',
  12: 'space-x-12',
  16: 'space-x-16',
  20: 'space-x-20',
  24: 'space-x-24',
  32: 'space-x-32',
};

// Space Y (vertical only)
const SPACE_Y_SCALE = {
  0: 'space-y-0',
  0.5: 'space-y-0.5',
  1: 'space-y-1',
  1.5: 'space-y-1.5',
  2: 'space-y-2',
  2.5: 'space-y-2.5',
  3: 'space-y-3',
  4: 'space-y-4',
  5: 'space-y-5',
  6: 'space-y-6',
  8: 'space-y-8',
  10: 'space-y-10',
  12: 'space-y-12',
  16: 'space-y-16',
  20: 'space-y-20',
  24: 'space-y-24',
  32: 'space-y-32',
  // Semantic aliases
  xs: 'space-y-1',
  sm: 'space-y-2',
};

const SURFACE_PADDING = {
  none: 'p-0',
  0.5: 'p-0.5',
  1: 'p-1',
  1.5: 'p-1.5',
  2: 'p-2',
  2.5: 'p-2.5',
  3: 'p-3',
  3.5: 'p-3.5',
  4: 'p-4',
  5: 'p-5',
  6: 'p-6',
  7: 'p-7',
  8: 'p-8',
  9: 'p-9',
  10: 'p-10',
  11: 'p-11',
  12: 'p-12',
  13: 'p-13',
  14: 'p-14',
  15: 'p-15',
  16: 'p-16',
  17: 'p-17',
  18: 'p-18',
  19: 'p-19',
  20: 'p-20',
  21: 'p-21',
  22: 'p-22',
  23: 'p-23',
  24: 'p-24',
  25: 'p-25',
  26: 'p-26',
  27: 'p-27',
  28: 'p-28',
  29: 'p-29',
  30: 'p-30',
  31: 'p-31',
  32: 'p-32',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
  xl: 'p-12',
  '2xl': 'p-16',
};

// Padding X (horizontal)
const PADDING_X = {
  0: 'px-0',
  0.5: 'px-0.5',
  1: 'px-1',
  1.5: 'px-1.5',
  2: 'px-2',
  2.5: 'px-2.5',
  3: 'px-3',
  3.5: 'px-3.5',
  4: 'px-4',
  5: 'px-5',
  6: 'px-6',
  7: 'px-7',
  8: 'px-8',
  9: 'px-9',
  10: 'px-10',
  11: 'px-11',
  12: 'px-12',
  13: 'px-13',
  14: 'px-14',
  15: 'px-15',
  16: 'px-16',
  17: 'px-17',
  18: 'px-18',
  19: 'px-19',
  20: 'px-20',
  21: 'px-21',
  22: 'px-22',
  23: 'px-23',
  24: 'px-24',
  25: 'px-25',
  26: 'px-26',
  27: 'px-27',
  28: 'px-28',
  29: 'px-29',
  30: 'px-30',
  31: 'px-31',
  32: 'px-32',
};

// Padding Y (vertical)
const PADDING_Y = {
  0: 'py-0',
  0.5: 'py-0.5',
  1: 'py-1',
  1.5: 'py-1.5',
  2: 'py-2',
  2.5: 'py-2.5',
  3: 'py-3',
  3.5: 'py-3.5',
  4: 'py-4',
  5: 'py-5',
  6: 'py-6',
  7: 'py-7',
  8: 'py-8',
  9: 'py-9',
  10: 'py-10',
  11: 'py-11',
  12: 'py-12',
  13: 'py-13',
  14: 'py-14',
  15: 'py-15',
  16: 'py-16',
  17: 'py-17',
  18: 'py-18',
  19: 'py-19',
  20: 'py-20',
  21: 'py-21',
  22: 'py-22',
  23: 'py-23',
  24: 'py-24',
  25: 'py-25',
  26: 'py-26',
  27: 'py-27',
  28: 'py-28',
  29: 'py-29',
  30: 'py-30',
  31: 'py-31',
  32: 'py-32',
};

// Padding Top
const PADDING_TOP = {
  0: 'pt-0',
  0.5: 'pt-0.5',
  1: 'pt-1',
  1.5: 'pt-1.5',
  2: 'pt-2',
  2.5: 'pt-2.5',
  3: 'pt-3',
  3.5: 'pt-3.5',
  4: 'pt-4',
  5: 'pt-5',
  6: 'pt-6',
  7: 'pt-7',
  8: 'pt-8',
  9: 'pt-9',
  10: 'pt-10',
  11: 'pt-11',
  12: 'pt-12',
  13: 'pt-13',
  14: 'pt-14',
  15: 'pt-15',
  16: 'pt-16',
  17: 'pt-17',
  18: 'pt-18',
  19: 'pt-19',
  20: 'pt-20',
  21: 'pt-21',
  22: 'pt-22',
  23: 'pt-23',
  24: 'pt-24',
  25: 'pt-25',
  26: 'pt-26',
  27: 'pt-27',
  28: 'pt-28',
  29: 'pt-29',
  30: 'pt-30',
  31: 'pt-31',
  32: 'pt-32',
  36: 'pt-36',
  37: 'pt-37',
  38: 'pt-38',
  40: 'pt-40',
};

// Padding Bottom
const PADDING_BOTTOM = {
  0: 'pb-0',
  0.5: 'pb-0.5',
  1: 'pb-1',
  1.5: 'pb-1.5',
  2: 'pb-2',
  2.5: 'pb-2.5',
  3: 'pb-3',
  3.5: 'pb-3.5',
  4: 'pb-4',
  5: 'pb-5',
  6: 'pb-6',
  7: 'pb-7',
  8: 'pb-8',
  9: 'pb-9',
  10: 'pb-10',
  11: 'pb-11',
  12: 'pb-12',
  13: 'pb-13',
  14: 'pb-14',
  15: 'pb-15',
  16: 'pb-16',
  17: 'pb-17',
  18: 'pb-18',
  19: 'pb-19',
  20: 'pb-20',
  21: 'pb-21',
  22: 'pb-22',
  23: 'pb-23',
  24: 'pb-24',
  25: 'pb-25',
  26: 'pb-26',
  27: 'pb-27',
  28: 'pb-28',
  29: 'pb-29',
  30: 'pb-30',
  31: 'pb-31',
  32: 'pb-32',
  safe: 'pb-safe',
};

// Padding Right
const PADDING_RIGHT = {
  0: 'pr-0',
  0.5: 'pr-0.5',
  1: 'pr-1',
  1.5: 'pr-1.5',
  2: 'pr-2',
  2.5: 'pr-2.5',
  3: 'pr-3',
  3.5: 'pr-3.5',
  4: 'pr-4',
  5: 'pr-5',
  6: 'pr-6',
  7: 'pr-7',
  8: 'pr-8',
  9: 'pr-9',
  10: 'pr-10',
  11: 'pr-11',
  12: 'pr-12',
  13: 'pr-13',
  14: 'pr-14',
  15: 'pr-15',
  16: 'pr-16',
  17: 'pr-17',
  18: 'pr-18',
  19: 'pr-19',
  20: 'pr-20',
  21: 'pr-21',
  22: 'pr-22',
  23: 'pr-23',
  24: 'pr-24',
  25: 'pr-25',
  26: 'pr-26',
  27: 'pr-27',
  28: 'pr-28',
  29: 'pr-29',
  30: 'pr-30',
  31: 'pr-31',
  32: 'pr-32',
};

// Padding Left
const PADDING_LEFT = {
  0: 'pl-0',
  0.5: 'pl-0.5',
  1: 'pl-1',
  1.5: 'pl-1.5',
  2: 'pl-2',
  2.5: 'pl-2.5',
  3: 'pl-3',
  3.5: 'pl-3.5',
  4: 'pl-4',
  5: 'pl-5',
  6: 'pl-6',
  7: 'pl-7',
  8: 'pl-8',
  9: 'pl-9',
  10: 'pl-10',
  11: 'pl-11',
  12: 'pl-12',
  13: 'pl-13',
  14: 'pl-14',
  15: 'pl-15',
  16: 'pl-16',
  17: 'pl-17',
  18: 'pl-18',
  19: 'pl-19',
  20: 'pl-20',
  21: 'pl-21',
  22: 'pl-22',
  23: 'pl-23',
  24: 'pl-24',
  25: 'pl-25',
  26: 'pl-26',
  27: 'pl-27',
  28: 'pl-28',
  29: 'pl-29',
  30: 'pl-30',
  31: 'pl-31',
  32: 'pl-32',
};

// Margin X (horizontal)
const MARGIN_X = {
  0: 'mx-0',
  0.5: 'mx-0.5',
  1: 'mx-1',
  1.5: 'mx-1.5',
  2: 'mx-2',
  2.5: 'mx-2.5',
  3: 'mx-3',
  3.5: 'mx-3.5',
  4: 'mx-4',
  4.5: 'mx-4.5',
  5: 'mx-5',
  6: 'mx-6',
  7: 'mx-7',
  8: 'mx-8',
  9: 'mx-9',
  10: 'mx-10',
  11: 'mx-11',
  12: 'mx-12',
  13: 'mx-13',
  14: 'mx-14',
  15: 'mx-15',
  16: 'mx-16',
  17: 'mx-17',
  18: 'mx-18',
  19: 'mx-19',
  20: 'mx-20',
  21: 'mx-21',
  22: 'mx-22',
  23: 'mx-23',
  24: 'mx-24',
  25: 'mx-25',
  26: 'mx-26',
  27: 'mx-27',
  28: 'mx-28',
  29: 'mx-29',
  30: 'mx-30',
  31: 'mx-31',
  32: 'mx-32',
  auto: 'mx-auto',
};

// Margin Y (vertical)
const MARGIN_Y = {
  0: 'my-0',
  0.5: 'my-0.5',
  1: 'my-1',
  1.5: 'my-1.5',
  2: 'my-2',
  2.5: 'my-2.5',
  3: 'my-3',
  3.5: 'my-3.5',
  4: 'my-4',
  4.5: 'my-4.5',
  5: 'my-5',
  6: 'my-6',
  7: 'my-7',
  8: 'my-8',
  9: 'my-9',
  10: 'my-10',
  11: 'my-11',
  12: 'my-12',
  13: 'my-13',
  14: 'my-14',
  15: 'my-15',
  16: 'my-16',
  17: 'my-17',
  18: 'my-18',
  19: 'my-19',
  20: 'my-20',
  21: 'my-21',
  22: 'my-22',
  23: 'my-23',
  24: 'my-24',
  25: 'my-25',
  26: 'my-26',
  27: 'my-27',
  28: 'my-28',
  29: 'my-29',
  30: 'my-30',
  31: 'my-31',
  32: 'my-32',
};

// Margin Left
const MARGIN_LEFT = {
  0: 'ml-0',
  0.5: 'ml-0.5',
  1: 'ml-1',
  1.5: 'ml-1.5',
  2: 'ml-2',
  2.5: 'ml-2.5',
  3: 'ml-3',
  3.5: 'ml-3.5',
  4: 'ml-4',
  4.5: 'ml-4.5',
  5: 'ml-5',
  6: 'ml-6',
  7: 'ml-7',
  8: 'ml-8',
  9: 'ml-9',
  10: 'ml-10',
  11: 'ml-11',
  12: 'ml-12',
  13: 'ml-13',
  14: 'ml-14',
  15: 'ml-15',
  16: 'ml-16',
  17: 'ml-17',
  18: 'ml-18',
  19: 'ml-19',
  20: 'ml-20',
  21: 'ml-21',
  22: 'ml-22',
  23: 'ml-23',
  24: 'ml-24',
  25: 'ml-25',
  26: 'ml-26',
  27: 'ml-27',
  28: 'ml-28',
  29: 'ml-29',
  30: 'ml-30',
  31: 'ml-31',
  32: 'ml-32',
};

// Margin Right
const MARGIN_RIGHT = {
  0: 'mr-0',
  0.5: 'mr-0.5',
  1: 'mr-1',
  1.5: 'mr-1.5',
  2: 'mr-2',
  2.5: 'mr-2.5',
  3: 'mr-3',
  3.5: 'mr-3.5',
  4: 'mr-4',
  4.5: 'mr-4.5',
  5: 'mr-5',
  6: 'mr-6',
  7: 'mr-7',
  8: 'mr-8',
  9: 'mr-9',
  10: 'mr-10',
  11: 'mr-11',
  12: 'mr-12',
  13: 'mr-13',
  14: 'mr-14',
  15: 'mr-15',
  16: 'mr-16',
  17: 'mr-17',
  18: 'mr-18',
  19: 'mr-19',
  20: 'mr-20',
  21: 'mr-21',
  22: 'mr-22',
  23: 'mr-23',
  24: 'mr-24',
  25: 'mr-25',
  26: 'mr-26',
  27: 'mr-27',
  28: 'mr-28',
  29: 'mr-29',
  30: 'mr-30',
  31: 'mr-31',
  32: 'mr-32',
};

// Margin Bottom
const MARGIN_BOTTOM = {
  0: 'mb-0',
  0.5: 'mb-0.5',
  1: 'mb-1',
  1.5: 'mb-1.5',
  '1.5rem': 'mb-[1.5rem]',
  2: 'mb-2',
  2.5: 'mb-2.5',
  3: 'mb-3',
  3.5: 'mb-3.5',
  4: 'mb-4',
  4.5: 'mb-4.5',
  5: 'mb-5',
  6: 'mb-6',
  7: 'mb-7',
  8: 'mb-8',
  9: 'mb-9',
  10: 'mb-10',
  11: 'mb-11',
  12: 'mb-12',
  13: 'mb-13',
  14: 'mb-14',
  15: 'mb-15',
  16: 'mb-16',
  17: 'mb-17',
  18: 'mb-18',
  19: 'mb-19',
  20: 'mb-20',
  21: 'mb-21',
  22: 'mb-22',
  23: 'mb-23',
  24: 'mb-24',
  25: 'mb-25',
  26: 'mb-26',
  27: 'mb-27',
  28: 'mb-28',
  29: 'mb-29',
  30: 'mb-30',
  31: 'mb-31',
  32: 'mb-32',
};

// Margin Top
const MARGIN_TOP = {
  0: 'mt-0',
  0.5: 'mt-0.5',
  1: 'mt-1',
  1.5: 'mt-1.5',
  '1.5rem': 'mt-[1.5rem]',
  2: 'mt-2',
  2.5: 'mt-2.5',
  3: 'mt-3',
  3.5: 'mt-3.5',
  4: 'mt-4',
  4.5: 'mt-4.5',
  5: 'mt-5',
  6: 'mt-6',
  7: 'mt-7',
  8: 'mt-8',
  9: 'mt-9',
  10: 'mt-10',
  11: 'mt-11',
  12: 'mt-12',
  13: 'mt-13',
  14: 'mt-14',
  15: 'mt-15',
  16: 'mt-16',
  17: 'mt-17',
  18: 'mt-18',
  19: 'mt-19',
  20: 'mt-20',
  21: 'mt-21',
  22: 'mt-22',
  23: 'mt-23',
  24: 'mt-24',
  25: 'mt-25',
  26: 'mt-26',
  27: 'mt-27',
  28: 'mt-28',
  29: 'mt-29',
  30: 'mt-30',
  31: 'mt-31',
  32: 'mt-32',
  auto: 'mt-auto',
};

// Scroll margin top for scroll anchoring
const SCROLL_MARGIN_TOP = {
  20: 'scroll-mt-20',
};

// Responsive margin top for first-child elements
const RESPONSIVE_FIRST_MARGIN_TOP = {
  sm: {
    6: 'first:mt-6',
    8: 'first:mt-8',
  },
  lg: {
    12: 'first:mt-12',
  },
};

// Negative Margins
const NEGATIVE_MARGIN_X = {
  1: '-mx-1',
  1.5: '-mx-1.5',
  2: '-mx-2',
  3: '-mx-3',
  4: '-mx-4',
  6: '-mx-6',
  8: '-mx-8',
  16: '-mx-16',
};

const NEGATIVE_MARGIN_Y = {
  1: '-my-1',
  2: '-my-2',
  3: '-my-3',
  4: '-my-4',
  6: '-my-6',
  8: '-my-8',
  16: '-my-16',
};

const NEGATIVE_MARGIN_TOP = {
  1: '-mt-1',
  2: '-mt-2',
  3: '-mt-3',
  4: '-mt-4',
  6: '-mt-6',
  8: '-mt-8',
  16: '-mt-16',
};

const NEGATIVE_MARGIN_RIGHT = {
  16: '-mr-16',
};

const NEGATIVE_MARGIN_BOTTOM = {
  0.5: '-mb-0.5',
  1: '-mb-1',
  1.5: '-mb-1.5',
  2: '-mb-2',
  2.5: '-mb-2.5',
  3: '-mb-3',
  3.5: '-mb-3.5',
  4: '-mb-4',
  5: '-mb-5',
  6: '-mb-6',
  8: '-mb-8',
  12: '-mb-12',
  16: '-mb-16',
  20: '-mb-20',
  24: '-mb-24',
};

// Aspect ratio scale
const ASPECT_RATIO = {
  video: 'aspect-video',
  square: 'aspect-square',
  portrait: 'aspect-portrait',
  landscape: 'aspect-landscape',
  wide: 'aspect-wide',
  '4/3': 'aspect-[4/3]',
};

// Blur scale
const BLUR_SCALE = {
  none: 'blur-none',
  sm: 'blur-sm',
  md: 'blur-md',
  lg: 'blur-lg',
  xl: 'blur-xl',
  '2xl': 'blur-2xl',
  '3xl': 'blur-3xl',
};

// Duration scale for transitions and animations
const DURATION_SCALE = {
  instant: 'duration-75', // Immediate feedback, tooltips
  fast: 'duration-150', // Cards, dropdowns, subtle animations
  normal: 'duration-200', // Buttons, form controls, primary interactions
  slow: 'duration-300', // Large animations, page transitions
  slower: 'duration-500', // Hero animations, complex transitions
  slowest: 'duration-700', // Page entrances, major layout changes
  // Additional commonly used durations
  100: 'duration-100',
  400: 'duration-400',
  600: 'duration-600',
  800: 'duration-800',
};

// Transform/translate scale for positioning elements
const TRANSFORM_SCALE = {
  'translate-y': {
    '2px': 'translate-y-[2px]',
    '3px': 'translate-y-[3px]',
    '-2px': '-translate-y-[2px]',
    '-3px': '-translate-y-[3px]',
    '50%': 'translate-y-[-50%]',
    '-50%': '-translate-y-1/2',
    full: '-translate-y-full',
  },
  'translate-x': {
    px: 'translate-x-px',
    '50%': '-translate-x-1/2',
    0: 'translate-x-0',
  },
  rotate: {
    45: 'rotate-45',
    '-45': '-rotate-45',
  },
  scale: {
    150: 'scale-150',
  },
  transform: 'transform',
};

const SURFACE_CONTENT_TOP_PADDING = {
  sm: 'first:pt-4',
  md: 'first:pt-6',
  lg: 'first:pt-8',
  xl: 'first:pt-12',
};

const SURFACE_HEADER_PADDING = {
  none: 'p-0',
  sm: 'p-4 pb-3',
  md: 'p-6 pb-4',
  lg: 'p-8 pb-5',
  xl: 'p-12 pb-7',
};

const SURFACE_FOOTER_PADDING = {
  none: 'p-0',
  sm: 'p-4 pt-0',
  md: 'p-6 pt-0',
  lg: 'p-8 pt-0',
  xl: 'p-12 pt-0',
};

const CONTAINER_WIDTHS = {
  xs: 'max-w-xl',
  sm: 'max-w-3xl',
  md: 'max-w-5xl',
  'screen-sm': 'max-w-sm',
  'screen-md': 'max-w-md',
  lg: 'max-w-6xl',
  xl: 'max-w-7xl',
  '2xl': 'max-w-2xl',
  none: 'max-w-none',
  full: 'max-w-[100vw]',
  // Responsive viewport widths for specific UI patterns
  responsiveText: 'max-w-[92vw] sm:max-w-none',
  selectDropdown: 'max-w-[calc(100vw-1.5rem)] sm:max-w-md',
  responsiveContainer: 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8',
};

const CONTAINER_PADDING = {
  none: 'px-0',
  xs: 'px-3 sm:px-4',
  sm: 'px-4 sm:px-6',
  md: 'px-4 sm:px-6 lg:px-8',
  lg: 'px-6 sm:px-8 lg:px-12',
};

const SECTION_VERTICAL_PADDING = {
  none: 'py-0',
  xs: 'py-6',
  sm: 'py-8',
  md: 'py-12',
  lg: 'py-16',
  xl: 'py-24',
  '2xl': 'py-32',
  '3xl': 'py-40',
  responsive: 'py-12 sm:py-16 lg:py-20',
  compact: 'py-3',
  normal: 'py-4',
  generous: 'py-6',
};

const SECTION_BACKGROUNDS = {
  default: '',
  subtle: 'bg-slate-50',
  muted: 'bg-muted/60',
  surface: 'bg-white',
  gradient: 'bg-gradient-to-br from-slate-50 via-white to-slate-100',
};

// Semantic layout tokens for common patterns
const LAYOUT_TOKENS = {
  // Container patterns
  container: {
    maxWidth: {
      content: 'max-w-4xl',
      section: 'max-w-7xl',
      narrow: 'max-w-3xl',
      wide: 'max-w-full',
    },
    padding: {
      responsive: 'px-4 sm:px-6 lg:px-8',
      compact: 'px-4 sm:px-6',
      wide: 'px-6 sm:px-8 lg:px-12',
    },
  },

  // Content spacing patterns
  contentSpacing: {
    section: 'space-y-8',
    card: 'space-y-6',
    compact: 'space-y-4',
    minimal: 'space-y-2',
  },

  // Grid patterns
  grid: {
    gaps: {
      card: 'gap-6',
      compact: 'gap-4',
      minimal: 'gap-2',
    },
    columns: {
      featuredCards: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
      statsCards: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
      treatmentCards: 'grid-cols-1 md:grid-cols-2',
      blogCards: 'grid-cols-1 md:grid-cols-3',
    },
  },

  // Card height patterns
  cardHeights: {
    featured: 'h-80',
    stat: 'h-32',
    compact: 'h-20',
    standard: 'h-64',
  },

  // Interactive element heights - semantic naming for buttons, inputs, etc.
  interactiveHeights: INTERACTIVE_HEIGHTS,

  // Layout spacing patterns - semantic spacing for common layout needs
  layoutSpacing: {
    container: 'px-4 sm:px-6 lg:px-8', // Page container horizontal padding
    section: 'py-12 sm:py-16 lg:py-20', // Section vertical spacing
    content: 'space-y-6', // Content block spacing
    compact: 'space-y-4', // Tight content spacing
    minimal: 'space-y-2', // Minimal content spacing
  },

  // Section padding patterns - semantic vertical spacing for sections
  sectionPadding: {
    compact: 'py-8 sm:py-12', // Small sections, footers
    normal: 'py-12 sm:py-16', // Standard sections
    generous: 'py-16 sm:py-20 lg:py-24', // Hero sections, major content
    loading: 'py-20', // Loading states, empty states
    minimal: 'py-6 sm:py-8', // Small components, cards
  },

  // Layout patterns for common page structures
  layout: {
    sidebar: {
      mainContent: 'lg:col-span-2',
      sidebar: 'lg:col-span-1',
      fullWidth: 'col-span-full',
    },
    header: {
      breadcrumb: 'flex items-center gap-2 text-sm text-muted-foreground mb-4',
      actions: 'flex items-center gap-2 ml-auto',
    },
    footer: {
      links: 'grid grid-cols-2 md:grid-cols-4 gap-6',
      social: 'flex items-center gap-4',
    },
  },

  // Skeleton patterns for consistent loading states
  skeleton: {
    text: {
      xs: 'h-3 w-16 rounded ${COLOR_SCALE["skeleton-bg"]} ${RADIUS_SCALE.md} shimmer-effect',
      sm: 'h-4 w-24 rounded ${COLOR_SCALE["skeleton-bg"]} ${RADIUS_SCALE.md} shimmer-effect',
      md: 'h-4 w-32 rounded ${COLOR_SCALE["skeleton-bg"]} ${RADIUS_SCALE.md} shimmer-effect',
      lg: 'h-5 w-40 rounded ${COLOR_SCALE["skeleton-bg"]} ${RADIUS_SCALE.md} shimmer-effect',
      xl: 'h-6 w-56 rounded ${COLOR_SCALE["skeleton-bg"]} ${RADIUS_SCALE.md} shimmer-effect',
    },
    avatar: {
      xs: 'h-6 w-6 rounded-full ${COLOR_SCALE["skeleton-bg"]} ${RADIUS_SCALE.md} shimmer-effect',
      sm: 'h-8 w-8 rounded-full ${COLOR_SCALE["skeleton-bg"]} ${RADIUS_SCALE.md} shimmer-effect',
      md: 'h-10 w-10 rounded-full ${COLOR_SCALE["skeleton-bg"]} ${RADIUS_SCALE.md} shimmer-effect',
      lg: 'h-12 w-12 rounded-full ${COLOR_SCALE["skeleton-bg"]} ${RADIUS_SCALE.md} shimmer-effect',
      xl: 'h-16 w-16 rounded-full ${COLOR_SCALE["skeleton-bg"]} ${RADIUS_SCALE.md} shimmer-effect',
    },
    card: '${COLOR_SCALE["skeleton-bg"]} ${RADIUS_SCALE.md} shimmer-effect',
    button: 'h-10 w-20 rounded ${COLOR_SCALE["skeleton-bg"]} ${RADIUS_SCALE.md} shimmer-effect',
  },
};

const GRID_BASE_COLUMNS = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
  5: 'grid-cols-5',
  6: 'grid-cols-6',
};

const GRID_JUSTIFY_SELF = {
  start: 'justify-self-start',
  center: 'justify-self-center',
  end: 'justify-self-end',
  stretch: 'justify-self-stretch',
};

const GRID_RESPONSIVE_COLUMNS = {
  sm: {
    1: 'sm:grid-cols-1',
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-3',
    4: 'sm:grid-cols-4',
    5: 'sm:grid-cols-5',
    6: 'sm:grid-cols-6',
  },
  md: {
    1: 'md:grid-cols-1',
    2: 'md:grid-cols-2',
    3: 'md:grid-cols-3',
    4: 'md:grid-cols-4',
    5: 'md:grid-cols-5',
    6: 'md:grid-cols-6',
  },
  lg: {
    1: 'lg:grid-cols-1',
    2: 'lg:grid-cols-2',
    3: 'lg:grid-cols-3',
    4: 'lg:grid-cols-4',
    5: 'lg:grid-cols-5',
    6: 'lg:grid-cols-6',
  },
  xl: {
    1: 'xl:grid-cols-1',
    2: 'xl:grid-cols-2',
    3: 'xl:grid-cols-3',
    4: 'xl:grid-cols-4',
    5: 'xl:grid-cols-5',
    6: 'xl:grid-cols-6',
  },
};

const GRID_TEMPLATE_COLUMNS = {
  '1fr_280px': 'grid-cols-[1fr_280px]',
};

const GRID_AUTO_ROWS = {
  none: '',
  min: 'auto-rows-min',
  max: 'auto-rows-max',
  fr: 'auto-rows-fr',
};

// Typography scale and weights
// Standardized scale aligned with Tailwind config (includes custom 3xs/2xs)
const TYPE_SCALE = {
  '3xs': 'text-3xs', // 10px
  '2xs': 'text-2xs', // 11px
  xs: 'text-xs', // 12px
  xs13: 'text-xs13', // 13px - Custom size for detailed text
  sm: 'text-sm', // 14px
  base: 'text-base', // 16px
  lg: 'text-lg', // 18px
  xl: 'text-xl', // 20px
  '2xl': 'text-2xl', // 24px
  '3xl': 'text-3xl', // 30px
  '4xl': 'text-4xl', // 36px
  '5xl': 'text-5xl', // 48px
  '6xl': 'text-6xl', // 60px
  '7xl': 'text-7xl', // 72px
  '8xl': 'text-8xl', // 96px
  '9xl': 'text-9xl', // 128px
};

const TYPE_LEADING = {
  tight: 'leading-tight',
  snug: 'leading-snug',
  normal: 'leading-normal',
  relaxed: 'leading-relaxed',
  custom: 'leading-[1.1]',
};

const FONT_WEIGHTS = {
  light: 'font-light',
  normal: 'font-normal',
  regular: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
  mono: 'font-mono',
  // Numeric weights for CSS custom properties
  300: 'font-light',
  400: 'font-normal',
  500: 'font-medium',
  600: 'font-semibold',
  700: 'font-bold',
};

const TEXT_ALIGNMENT = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
  justify: 'text-justify',
  truncate: 'truncate',
};

const PROSE = {
  base: 'prose',
  sm: 'prose-sm',
  lg: 'prose-lg',
  xl: 'prose-xl',
  '2xl': 'prose-2xl',
  none: 'prose-none',
};

const WORD_BREAK = {
  'break-words': 'break-words',
  'break-all': 'break-all',
  'keep-all': 'keep-all',
  truncate: 'truncate',
};

const LETTER_SPACING = {
  tight: 'tracking-tight',
  normal: 'tracking-normal',
  wide: 'tracking-wide',
  wider: 'tracking-wider',
  widest: 'tracking-widest',
  // Custom values
  '-0.02em': '-tracking-[0.02em]',
  '-0.015em': '-tracking-[0.015em]',
  '-0.01em': '-tracking-[0.01em]',
};

const WHITESPACE = {
  normal: 'whitespace-normal',
  nowrap: 'whitespace-nowrap',
  pre: 'whitespace-pre',
  'pre-line': 'whitespace-pre-line',
  'pre-wrap': 'whitespace-pre-wrap',
};

const RESPONSIVE_WHITESPACE = {
  sm: {
    nowrap: 'sm:whitespace-nowrap',
  },
};

const DISPLAY = {
  block: 'block',
  inline: 'inline',
  inlineBlock: 'inline-block',
  flex: 'flex',
  inlineFlex: 'inline-flex',
  grid: 'grid',
  inlineGrid: 'inline-grid',
  none: 'hidden', // Tailwind uses 'hidden' instead of 'none'
  group: 'group',
};

// Responsive display utilities
const RESPONSIVE_DISPLAY = {
  hidden: {
    sm: 'sm:hidden',
    md: 'md:hidden',
    lg: 'lg:hidden',
    xl: 'xl:hidden',
  },
  block: {
    sm: 'sm:block',
    md: 'md:block',
    lg: 'lg:block',
    xl: 'xl:block',
  },
  flex: {
    sm: 'sm:flex',
    lg: 'lg:flex',
  },
  tableCell: {
    lg: 'lg:table-cell',
  },
  flexCol: {
    lg: 'lg:flex-col',
  },
  inline: {
    sm: 'sm:inline',
  },
};

const RESPONSIVE_POSITION_SCALE = {
  static: {
    sm: 'sm:static',
  },
  sticky: {
    lg: 'lg:sticky',
  },
  top: {
    lg: {
      20: 'lg:top-20',
      24: 'lg:top-24',
    },
  },
  bottom: {
    lg: {
      auto: 'lg:bottom-auto',
    },
  },
  left: {
    lg: {
      4: 'lg:left-4',
    },
  },
  selfStart: {
    lg: 'lg:self-start',
  },
  marginLeft: {
    sm: {
      auto: 'sm:ml-auto',
    },
  },
  width: {
    sm: {
      auto: 'sm:w-auto',
    },
  },
};

const RESPONSIVE_GRID = {
  lg: {
    grid: 'lg:grid',
    gridTemplateColumns: {
      '1fr_280px': 'lg:grid-cols-[1fr_280px]',
    },
    gap: {
      8: 'lg:gap-8',
    },
  },
};

const RESPONSIVE_COL_SPAN = {
  md: {
    1: 'md:col-span-1',
    2: 'md:col-span-2',
    3: 'md:col-span-3',
    4: 'md:col-span-4',
    5: 'md:col-span-5',
    6: 'md:col-span-6',
  },
  lg: {
    1: 'lg:col-span-1',
    2: 'lg:col-span-2',
    3: 'lg:col-span-3',
    4: 'lg:col-span-4',
    5: 'lg:col-span-5',
    6: 'lg:col-span-6',
  },
  combined: {
    col1Md2: 'col-span-1 md:col-span-2',
  },
};

const TEXT_TRANSFORM = {
  uppercase: 'uppercase',
  lowercase: 'lowercase',
  capitalize: 'capitalize',
  none: 'normal-case', // Tailwind uses 'normal-case' instead of 'none'
};

const TEXT_STYLING = {
  transparent: 'text-transparent',
  clipText: 'bg-clip-text',
  lineClamp1: 'line-clamp-1',
  italic: 'italic',
};

const TEXT_DECORATION = {
  underline: 'underline',
  'underline-offset-4': 'underline-offset-4',
  'no-underline': 'no-underline',
};

const LINE_HEIGHT = {
  none: 'leading-none',
  tight: 'leading-tight',
  snug: 'leading-snug',
  normal: 'leading-normal',
  relaxed: 'leading-relaxed',
  loose: 'leading-loose',
};

const LINE_CLAMP = {
  1: 'line-clamp-1',
  2: 'line-clamp-2',
  3: 'line-clamp-3',
  4: 'line-clamp-4',
  5: 'line-clamp-5',
  6: 'line-clamp-6',
  none: 'line-clamp-none',
};

const FLEX_UTILITIES = {
  flex: 'flex',
  flexCol: 'flex flex-col',
  flexRow: 'flex flex-row',
  flexItemsCenter: 'flex items-center',
  flexWrap: 'flex flex-wrap',
  flex1: 'flex-1',
  flexAuto: 'flex-auto',
  flexInitial: 'flex-initial',
  flexNone: 'flex-none',
  flexGrow: 'flex-grow',
  selfStart: 'self-start',
};

const ANIMATIONS = {
  pulse: 'animate-pulse',
  spin: 'animate-spin',
  ping: 'animate-ping',
  bounce: 'animate-bounce',
};

// Interactive State Tokens - Standardized across all components
const INTERACTIVE_STATES = {
  // Transition durations - semantic names for consistent timing
  duration: {
    instant: 'duration-75', // Immediate feedback, tooltips
    fast: 'duration-150', // Cards, dropdowns, subtle animations
    normal: 'duration-200', // Buttons, form controls, primary interactions
    slow: 'duration-300', // Large animations, page transitions
    slower: 'duration-500', // Hero animations, complex transitions
    slowest: 'duration-700', // Page entrances, major layout changes
  },

  // Shadow elevations for hover states
  shadows: {
    subtle: 'hover:shadow-md', // Standard buttons, cards
    elevated: 'hover:shadow-lg', // Elevated surfaces, modals
    strong: 'hover:shadow-xl', // Prominent CTAs, floating elements
  },

  // Focus-visible rings (following WCAG guidelines)
  focus: {
    ring: 'focus-visible:ring-2 focus-visible:ring-primary/40',
    offset: 'focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    outline: 'focus-visible:outline-none',
  },

  // Disabled states
  disabled: {
    opacity: 'disabled:opacity-60',
    pointer: 'disabled:pointer-events-none',
    cursor: 'disabled:cursor-not-allowed',
  },

  // Active/pressed states
  active: {
    button: 'active:scale-95', // Buttons, form controls
    card: 'active:translate-y-[1px]', // Cards, surfaces
  },

  // Group hover states
  groupHover: {
    opacity100: 'group-hover:opacity-100', // Show on group hover
  },

  // Hover transforms
  hover: {
    lift: 'hover:-translate-y-0.5', // Subtle lift for buttons/cards
    liftStrong: 'hover:-translate-y-1', // Stronger lift for CTAs
    scale: 'hover:scale-[1.02]', // Subtle scale for cards
    scale110: 'hover:scale-[1.1]', // Medium scale for interactive elements
    opacity80: 'hover:opacity-80', // Reduced opacity on hover
  },

  // Common transition patterns
  transitions: {
    colors: 'transition-colors duration-200', // Color changes, hover states
    all: 'transition-all duration-200', // Multiple properties
    fast: 'transition-all duration-150', // Quick transitions
    slow: 'transition-all duration-300', // Slower transitions
    opacity: 'transition-opacity', // Opacity transitions
    transform: 'transition-transform duration-200', // Transform transitions
  },

  // Ring styles for focus and decorative elements
  rings: {
    profile: 'ring-6 sm:ring-8 ring-white', // Profile image rings
    focus: 'ring-2 ring-blue-500/60', // Focus rings
    hover: 'ring-blue-100', // Hover ring colors
  },
};

// Global z-index scale for consistent layering across the app
// Keep values aligned with Tailwind conventions (z-10..z-50) and reserve higher only when necessary
const Z_INDEX = {
  base: 0, // default documents
  content: 1, // positioned content
  controls: 10, // sticky minor controls
  header: 20, // headers/navs
  dropdown: 30, // menus, popovers, tooltips
  overlay: 40, // toasts, progress bars above header
  overlay45: 45, // custom overlay level
  modal: 50, // modals and lightboxes
  modalHigh: 100, // higher modal level
  z10: 10, // standard z-index level
  jump: 10, // jump menu and backdrop (must be below header at 20 and dropdowns at 11000)
  highest: 11001, // extremely rare, reserved
  zBase: 10, // base z-index for positioned elements
  progress: 45, // progress navigation elements
};

// Color scale for semantic design system colors
const COLOR_SCALE = {
  // Background colors
  background: 'bg-background',
  surface: 'bg-white',
  'surface-muted': 'bg-slate-50',
  'surface-subtle': 'bg-slate-25',
  popover: 'bg-popover',

  // Black color variants
  black: {
    0: 'bg-black/0',
    20: 'bg-black/20',
    30: 'bg-black/30',
    40: 'bg-black/40',
    50: 'bg-black/50',
    60: 'bg-black/60',
    70: 'bg-black/70',
    80: 'bg-black/80',
    90: 'bg-black/90',
    95: 'bg-black/95',
  },

  // Gradient backgrounds
  'gradient-divider': 'bg-gradient-to-r from-transparent via-gray-200 to-transparent',
  'gradient-divider-dark': 'bg-gradient-to-r from-transparent via-gray-300 to-transparent',

  // Foreground/Text colors
  foreground: 'text-foreground',
  'foreground-muted': 'text-muted-foreground',
  'foreground-subtle': 'text-slate-600',
  'icon-muted': 'text-slate-500',
  'gray-500': 'text-gray-500',

  // Border colors
  border: 'border-border',
  'border-subtle': 'border-slate-200',
  'border-muted': 'border-slate-300',

  // Primary colors
  primary: 'text-primary',
  'primary-bg': 'bg-primary',
  'primary-border': 'border-primary',

  // Secondary colors
  secondary: 'text-secondary',
  'secondary-bg': 'bg-secondary',

  // Accent colors
  accent: 'text-accent',
  'accent-bg': 'bg-accent',

  // Destructive colors
  destructive: 'text-destructive',
  'destructive-bg': 'bg-destructive',
  'destructive-bg-hover': 'hover:bg-destructive/90',

  // Status colors
  success: 'text-green-600',
  'success-bg': 'bg-green-50',
  warning: 'text-yellow-600',
  'warning-bg': 'bg-yellow-50',
  error: 'text-red-600',
  'error-bg': 'bg-red-50',
  info: 'text-blue-600',
  'info-bg': 'bg-blue-50',

  // Interactive states
  hover: 'hover:bg-slate-50',
  active: 'active:bg-slate-100',
  focus: 'focus:bg-slate-50',

  // Header specific colors
  'header-light': 'text-gray-700 hover:bg-slate-25 hover:text-blue-600',
  'header-dark': 'text-white/80 hover:bg-white/10 hover:text-white',

  // Skeleton and loading colors
  'skeleton-bg': 'bg-gray-200',
  'skeleton-bg-light': 'bg-slate-25',
  'skeleton-glass': 'border-white/60 bg-white/65',
  'skeleton-glass-bg': 'from-white/80 via-white/60 to-white/30',
  'skeleton-glass-line': 'via-white/70',
  'skeleton-blue-blur': 'bg-blue-500/10',

  // Floating CTA gradients
  'cta-blue-gradient': 'bg-gradient-to-br from-blue-500/20 to-blue-600/30',
  'cta-blue-gradient-hover': 'hover:from-blue-500/25 hover:to-blue-600/35',
  'cta-mobile-gradient': 'bg-gradient-to-r from-gray-900 to-gray-800',

  // Keyboard shortcuts hint colors
  'shortcuts-bg': 'bg-gradient-to-br from-slate-800 to-slate-900',
  'shortcuts-header-bg': 'bg-slate-900/50',
  'shortcuts-border': 'border-slate-700',
  'shortcuts-text': 'text-white',
  'shortcuts-text-muted': 'text-slate-300',
  'shortcuts-text-accent': 'text-slate-200',
  'shortcuts-text-secondary': 'text-slate-500',
  'shortcuts-icon': 'text-blue-400',
  'shortcuts-hover': 'hover:bg-slate-700',

  // Toast notification colors
  'toast-success': 'text-green-600',
  'toast-error': 'text-red-600',
  'toast-info': 'text-foreground',

  // Tooltip colors
  'tooltip-bg': 'bg-gray-900',
  'tooltip-text': 'text-white',
  'tooltip-text-muted': 'text-gray-200',
  'tooltip-header-bg': 'bg-gray-800',
  'tooltip-border': 'border-gray-700',

  // Sidebar colors (mapped to CSS variables)
  'sidebar-bg': 'bg-sidebar',
  'sidebar-fg': 'text-sidebar-foreground',
  'sidebar-primary': 'text-sidebar-primary',
  'sidebar-primary-fg': 'text-sidebar-primary-foreground',
  'sidebar-accent': 'bg-sidebar-accent',
  'sidebar-accent-fg': 'text-sidebar-accent-foreground',
  'sidebar-border': 'border-sidebar-border',
  'sidebar-ring': 'ring-sidebar-ring',

  // Border gradients
  'border-gradient': 'via-gray-200',

  // Button specific colors
  'button-success': 'bg-green-600 text-white hover:bg-green-700 focus-visible:ring-green-500',
  'button-warning': 'bg-yellow-600 text-white hover:bg-yellow-700 focus-visible:ring-yellow-500',
  'button-info': 'bg-blue-500 text-white hover:bg-blue-600 focus-visible:ring-blue-400',
  'button-ai':
    'bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-600 text-white hover:from-yellow-500 hover:via-amber-600 hover:to-yellow-700 focus-visible:ring-yellow-500',

  // Form field specific colors
  'field-border': 'border-slate-200',
  'field-bg': 'bg-white',
  'field-text': 'text-slate-900',
  'field-placeholder': 'placeholder:text-slate-400',
  'field-focus-border': 'focus-visible:border-primary/70',
  'field-focus-ring': 'ring-primary/30',

  // Dialog/Sheet overlay
  'dialog-overlay': 'bg-black/70',
  'sheet-overlay': 'bg-black/80',
  'modal-overlay': 'bg-black',

  // Footer/Company specific colors
  'logo-gradient': 'from-gray-900 to-gray-700',
  'region-selector': 'text-gray-700 border-slate-200 hover:bg-slate-25',
  'dropdown-bg': 'bg-white/95 border border-slate-200',
  'dropdown-item':
    'text-gray-700 transition-colors duration-200 hover:bg-blue-50 hover:text-blue-600',
  'description-text': 'text-muted-foreground',

  // Admin component colors
  'admin-heading': 'text-foreground',
  'admin-subtext': 'text-muted-foreground',
  'admin-muted-text': 'text-slate-500',
  'admin-icon-bg': 'bg-blue-100',
  'admin-icon': 'text-blue-600',
  'admin-link': 'text-blue-600 hover:text-blue-700 hover:bg-blue-50',
  'admin-border': 'border-slate-200',
  'admin-border-subtle': 'border-black/10',
  'admin-section-bg': 'from-blue-50 to-indigo-50',
  'admin-card-bg': 'bg-slate-50',
  'clinic-icon-bg': 'bg-blue-100',

  // Tabs component colors
  'tabs-border': 'border-slate-200',
  'tabs-active-bg': 'bg-white',
  'tabs-active-ring': 'ring-slate-200',

  // Checkbox component colors
  'checkbox-unchecked': 'border-slate-200 bg-white',
  'checkbox-checked': 'border-blue-500 bg-blue-500 text-white',

  // Select component colors
  'select-border': 'border-slate-200',
  'select-separator': 'bg-slate-200',

  // Tooltip component colors

  // Accordion component colors
  'accordion-border': 'border-slate-200',
  'accordion-icon': 'text-muted-foreground',
  'accordion-content': 'text-gray-700',

  // NumberField component colors
  'number-field-border': 'border-slate-200',
  'number-field-bg': 'bg-white',
  'number-field-placeholder': 'placeholder:text-slate-500',
  'number-field-focus': 'focus-visible:ring-blue-500',
  'number-field-error': 'border-red-500 focus-visible:ring-red-500',
  'number-field-label': 'text-gray-700',
  'number-field-required': 'text-red-500',
  'number-field-spinner-border': 'border-slate-200',
  'number-field-spinner-hover': 'hover:bg-slate-50',
  'number-field-error-text': 'text-red-500',
  'number-field-helper-text': 'text-slate-500',

  // Pagination component colors
  'pagination-hint-bg': 'bg-gray-900',
  'pagination-hint-text': 'text-white',
  'pagination-hint-hover': 'hover:text-gray-300',

  // Admin clinic specific colors
  'admin-clinic-button': 'text-foreground bg-white border border-slate-200 hover:bg-slate-50 hover:border-slate-300 shadow-sm',

  // View mode toggle colors
  'view-toggle-active': 'bg-white text-foreground',
  'view-toggle-inactive': 'text-muted-foreground hover:text-foreground',
  'view-toggle-container': 'bg-slate-25',

  // Admin form colors
  'form-success-bg': 'bg-green-50',
  'form-success-border': 'border-green-200',
  'form-success-text': 'text-green-600',
  'form-success-text-dark': 'text-green-800',
  'form-active-icon': 'text-blue-600',
  'form-muted-bg': 'bg-slate-25',
  'form-active-border': 'border-blue-500',
  'form-active-bg': 'bg-blue-50',
  'form-inactive-border': 'border-slate-200 hover:border-gray-400',

  // Admin leaderboard colors
  'leaderboard-trophy': 'text-yellow-500',
  'leaderboard-medal': 'text-slate-500',
  'leaderboard-award': 'text-amber-600',
  'leaderboard-current-user': 'bg-primary/5 border-primary/20',
  'leaderboard-badge': 'bg-primary/10 text-primary',

  // Admin table colors
  'table-resize-handle': 'bg-slate-100 group-hover:bg-primary/60',
  'table-info-bg': 'bg-slate-50',
  'table-info-text': 'text-gray-700',
  'table-card': 'bg-white border border-slate-200',
  'table-header-row':
    'from-gray-50 to-gray-100/50 hover:from-gray-50 hover:to-gray-100/50 border-b border-slate-200',
  'table-header-border': 'border-slate-200',
  'table-header-text': 'text-gray-700',

  // Blog posts table colors
  'blog-category-default': 'bg-slate-25 text-gray-700',
  'blog-placeholder': 'bg-slate-25 text-slate-500',
  'blog-title': 'text-foreground',
  'blog-excerpt': 'text-slate-500',
  'blog-meta': 'text-muted-foreground',
  'blog-author-icon': 'text-indigo-500',
  'blog-author': 'text-gray-800',
  'blog-calendar-icon': 'text-blue-500',
  'blog-status-published': 'bg-yellow-100 text-yellow-800',
  'blog-status-draft': 'text-slate-500',

  // Admin translations colors
  'translations-loading': 'bg-blue-50 border border-blue-200',
  'translations-loading-icon': 'text-blue-600',
  'translations-loading-text': 'text-blue-900',

  // Admin XP bar colors
  'xp-dropdown': 'border border-slate-200 bg-white shadow-lg',
  'xp-menu-item': 'text-gray-700 hover:bg-slate-50',
  'xp-separator': 'border-slate-200',
  'xp-meta-text': 'text-slate-500',
  'xp-meta-heading': 'text-muted-foreground',

  // Medical procedures table colors
  'procedure-status-published': 'bg-green-50 text-green-700 border-green-200',
  'procedure-status-draft': 'bg-slate-25 text-muted-foreground border-slate-200',
  'procedure-card': 'border border-slate-200 shadow-md rounded-2xl',
  'procedure-image-placeholder': 'bg-slate-25 shadow-sm',
  'procedure-star': 'text-purple-600 fill-current',
  'procedure-title': 'text-foreground',
  'procedure-subtitle': 'text-slate-500',
  'procedure-description': 'text-muted-foreground',
  'procedure-badge': 'bg-gradient-to-br from-gray-100 to-gray-50 text-gray-700 border-slate-200/50',
  'procedure-count': 'text-blue-700',
  'procedure-meta': 'text-muted-foreground',
  'procedure-active': 'bg-green-500',
  'procedure-inactive': 'bg-gray-400',

  // Specialty category gradients
  'specialty-cosmetic': 'from-pink-50 to-rose-50 text-pink-700 border-pink-200/50',
  'specialty-dermatology': 'from-purple-50 to-violet-50 text-purple-700 border-purple-200/50',
  'specialty-dental': 'from-blue-50 to-cyan-50 text-blue-700 border-blue-200/50',
  'specialty-cardiac': 'from-red-50 to-rose-50 text-red-700 border-red-200/50',
  'specialty-orthopedic': 'from-green-50 to-emerald-50 text-green-700 border-green-200/50',
  'specialty-oncology': 'from-gray-50 to-slate-50 text-gray-700 border-slate-200/50',
  'specialty-neurology': 'from-indigo-50 to-blue-50 text-indigo-700 border-indigo-200/50',

  // Package table editor colors
  'package-star-full': 'fill-yellow-400 text-yellow-400',
  'package-star-empty': 'text-gray-300',
  'package-rating-text': 'text-muted-foreground',
  'package-action-primary': 'text-blue-600 border-blue-200 hover:bg-blue-50',
  'package-header-text': 'text-muted-foreground',
  'package-delete-button': 'text-red-500 hover:text-red-700 hover:bg-red-50',
  'package-mobile-card': 'border border-slate-200 bg-slate-50',
  'package-mobile-header': 'text-slate-500',
  'package-mobile-edit': 'text-blue-600 hover:text-blue-700',
  'package-mobile-select':
    'text-sm text-gray-700 px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-25',
  'package-table-row': 'bg-slate-50',
  'package-table-header': 'bg-slate-25 border-0',

  // Admin inquiries colors
  'inquiries-toggle-container': 'bg-white rounded-full border border-slate-200 p-1.5 shadow-sm',
  'inquiries-toggle-active': 'bg-gray-900 text-white shadow-md',
  'inquiries-toggle-inactive': 'text-gray-700 hover:text-foreground hover:bg-gray-200',
  'inquiries-view-toggle-container': 'bg-slate-25 p-1',
  'inquiries-view-toggle-active': 'bg-white text-foreground shadow-sm',
  'inquiries-view-toggle-inactive': 'text-muted-foreground hover:text-foreground',

  // Admin equipment colors
  'equipment-toggle-container': 'border border-slate-200 bg-slate-50/80 p-1 text-xs shadow-sm',
  'equipment-toggle-inactive': 'text-muted-foreground hover:text-foreground hover:bg-transparent',
  'equipment-select-border': 'border border-slate-200',

  // Clinics table - ProviderGridCard colors
  'clinic-card': 'border border-slate-200 rounded-2xl shadow-sm hover:shadow-lg',
  'clinic-icon': 'text-blue-300',
  'clinic-badge': 'bg-white/90 text-gray-800 border-0 shadow-md',
  'clinic-title': 'text-foreground',
  'clinic-type-badge': 'bg-purple-50 text-purple-700 border-purple-200',
  'clinic-action-button':
    'text-purple-600 border border-purple-200 rounded-md bg-white hover:bg-purple-50',
  'clinic-location': 'text-muted-foreground',
  'clinic-location-icon': 'text-slate-500',
  'clinic-description': 'text-slate-500',
  'clinic-meta': 'text-muted-foreground',
  'clinic-stats-bg': 'bg-blue-50',
  'clinic-stats-icon': 'text-blue-600',

  'table-row':
    'group border-b border-slate-200 hover:bg-gradient-to-r hover:from-blue-50/40 hover:to-indigo-50/20 transition-colors',
  'table-cell-border': 'border-slate-200',
  'table-expand-button':
    'border border-slate-200 bg-white shadow-sm text-muted-foreground hover:text-blue-600 hover:border-blue-200 hover:bg-blue-50',
  'table-building-icon': 'text-blue-300',
  'table-clinic-title': 'text-foreground',
  'table-meta-text': 'text-muted-foreground',
  'table-meta-icon': 'text-slate-500',
  'table-type-badge': 'bg-purple-50 text-purple-700 border-purple-200',
  'table-description': 'text-slate-500',
  'table-status-badge': 'bg-blue-50 text-blue-700 border-blue-200',
  'table-stats-bg': 'bg-blue-50 px-2 py-1',
  'table-stats-text': 'text-blue-700',
  'table-stats-header': 'text-blue-600',

  // Admin selector colors
  'admin-selector-active': 'bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-200',
  'admin-selector-assigned': 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100',
  'admin-selector-inactive': 'bg-slate-50 text-muted-foreground border-slate-200 hover:bg-slate-25',
  'admin-selector-button-active': 'bg-blue-600 border-blue-600',
  'admin-selector-button-inactive': 'border-slate-200',
  'admin-selector-email': 'text-slate-500',

  // Doctor form colors
  'doctor-form-section-border': 'border-slate-200',
  'doctor-form-drag-handle': 'hover:bg-slate-25',
  'doctor-form-drag-icon': 'text-slate-500',
  'doctor-form-section-title': 'text-gray-700',
  'doctor-form-status-published': 'bg-green-50 text-green-700 border-green-200',
  'doctor-form-status-draft': 'bg-blue-50 text-blue-700 border-blue-200',
  'doctor-form-footer-border': 'border-slate-200',

  // Treatment form colors
  'treatment-error-bg': 'bg-red-100 border border-red-400 text-red-700',

  // Form validation error states
  'form-error-text': 'text-red-600',
  'form-error-text-light': 'text-red-500',
  'form-error-text-dark': 'text-red-700',
  'form-error-text-darker': 'text-red-800',
  'form-error-bg': 'bg-red-50',
  'form-error-bg-hover': 'hover:bg-red-100',
  'form-error-border': 'border-red-200',
  'form-error-border-focus': 'border-red-300',
  'form-error-text-hover': 'hover:text-red-700',
  'form-error-text-hover-darker': 'hover:text-red-800',
  'treatment-translation-bg': 'bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200',
  'treatment-translation-icon': 'text-purple-600',
  'treatment-translation-title': 'text-foreground',
  'treatment-lang-published': 'bg-green-100 text-green-700 border border-green-300',
  'treatment-lang-draft': 'bg-blue-100 text-blue-700 border border-blue-300',
  'treatment-meta-text': 'text-muted-foreground',
  'treatment-arrow': 'text-slate-500',
  'treatment-progress-check': 'text-green-600',
  'treatment-progress-text': 'text-green-600',

  // Medical procedure form colors
  'procedure-form-submit': 'bg-blue-600 hover:bg-blue-700',

  // Partner analytics colors
  'analytics-title': 'text-foreground',
  'analytics-skeleton-bar': '${COLOR_SCALE["skeleton-bg"]} ${RADIUS_SCALE.md} shimmer-effect',
  'analytics-chart-placeholder': 'bg-slate-25 rounded-xl',

  // Chatbot analytics colors
  'chatbot-card-bg': 'bg-slate-50/50',
  'chatbot-header-title': 'text-slate-900',
  'chatbot-header-subtitle': 'text-slate-600',
  'chatbot-button':
    'bg-white/80 backdrop-blur-sm border-slate-200 hover:bg-white hover:border-slate-300',
  'chatbot-conversations-bg': 'bg-blue-100 group-hover:bg-blue-200',
  'chatbot-conversations-icon': 'text-blue-600',
  'chatbot-conversations-label': 'text-slate-500',
  'chatbot-conversations-value': 'text-slate-900',
  'chatbot-conversations-desc': 'text-slate-600',
  'chatbot-satisfaction-bg': 'bg-green-100 group-hover:bg-green-200',
  'chatbot-satisfaction-icon': 'text-green-600',
  'chatbot-satisfaction-label': 'text-slate-500',
  'chatbot-satisfaction-value': 'text-slate-900',
  'chatbot-satisfaction-desc': 'text-slate-600',
  'chatbot-rating-bg': 'bg-yellow-100 group-hover:bg-yellow-200',
  'chatbot-rating-icon': 'text-yellow-600',
  'chatbot-rating-label': 'text-slate-500',
  'chatbot-rating-value': 'text-slate-900',
  'chatbot-rating-desc': 'text-slate-600',

  // Badge specific colors
  'badge-primary': 'border-primary/30 bg-primary/10 text-primary',
  'badge-secondary': 'border-secondary/30 bg-secondary/20 text-secondary-foreground',
  'badge-muted': 'border-transparent bg-muted text-foreground',
  'badge-ghost': 'border-transparent bg-transparent text-foreground',
  'badge-outline': 'border-slate-300 bg-white text-slate-700',
  'badge-info': 'border-sky-200 bg-sky-50 text-sky-700',
  'badge-success': 'border-emerald-200 bg-emerald-50 text-emerald-700',
  'badge-warning': 'border-amber-200 bg-amber-50 text-amber-700',
  'badge-danger': 'border-destructive/30 bg-destructive/10 text-destructive',
  'badge-destructive': 'border-destructive/30 bg-destructive/10 text-destructive',
  'badge-elevated': 'border-transparent bg-white text-slate-900',

  // Additional semantic color tokens for common UI patterns
  'icon-purple': 'text-purple-600',
  'icon-indigo': 'text-indigo-600',
  'icon-blue': 'text-blue-600',
  'icon-green': 'text-green-600',
  'icon-red': 'text-red-600',
  'icon-yellow': 'text-yellow-600',
  'icon-gray': 'text-gray-600',
  'icon-slate': 'text-slate-600',

  // Interactive state colors (for buttons, links, hover states)
  'interactive-primary': 'text-blue-600',
  'interactive-primary-hover': 'hover:text-blue-700',
  'interactive-primary-bg': 'bg-blue-600',
  'interactive-primary-bg-hover': 'hover:bg-blue-700',
  'interactive-emerald': 'text-emerald-600',
  'interactive-emerald-hover': 'hover:text-emerald-700',
  'interactive-emerald-bg': 'bg-emerald-600',
  'interactive-emerald-bg-hover': 'hover:bg-emerald-700',
  'interactive-emerald-light': 'bg-emerald-50',
  'interactive-emerald-light-hover': 'hover:bg-emerald-100',
  'emerald-500': 'text-emerald-500',
  'emerald-200': 'text-emerald-200',
  'emerald-50': 'text-emerald-50',
  'emerald-600': 'text-emerald-600',
  'emerald-600-bg': 'bg-emerald-600',
  'emerald-700': 'text-emerald-700',
  'emerald-700-bg': 'bg-emerald-700',
  'emerald-800': 'text-emerald-800',
  'emerald-50-bg': 'bg-emerald-50',
  'emerald-50-bg-hover': 'hover:bg-emerald-50',
  'emerald-100-bg': 'bg-emerald-100',
  'emerald-100-ring': 'ring-emerald-100',
  'emerald-100-border': 'border-emerald-100',
  'emerald-200-bg': 'bg-emerald-200',
  'emerald-200-border': 'border-emerald-200',

  // Status colors for semantic states
  'status-success': 'text-green-600',
  'status-success-bg': 'bg-green-50',
  'status-success-bg-hover': 'hover:bg-green-50',
  'status-success-border': 'border-green-200',
  'status-warning': 'text-yellow-600',
  'status-warning-bg': 'bg-yellow-50',
  'status-warning-border': 'border-yellow-200',
  'status-error': 'text-red-600',
  'status-error-hover': 'hover:text-red-700',
  'status-error-bg': 'bg-red-50',
  'status-error-bg-hover': 'hover:bg-red-50',
  'status-error-border': 'border-red-200',
  'status-info': 'text-blue-600',
  'status-info-bg': 'bg-blue-50',
  'status-info-border': 'border-blue-200',

  // Additional semantic colors
  'status-orange': 'text-orange-600',
  'status-orange-bg': 'bg-orange-100',
  'status-orange-border': 'border-orange-200',
  'border-l-orange-500': 'border-l-orange-500',

  // Teal colors for secondary accents
  'status-teal': 'text-teal-600',
  'status-teal-bg': 'bg-teal-600',
  'status-teal-hover': 'hover:bg-teal-700',

  // Glass morphism variations
  'glass-light': 'bg-white/80',
  'glass-light-hover': 'hover:bg-white/90',
  'glass-dark': 'bg-black/60',
  'glass-dark-hover': 'hover:bg-black/70',

  // Overlay backgrounds
  'overlay-dark': 'bg-black/30',
  'overlay-dark-40': 'bg-black/40',
  'overlay-dark-hover': 'group-hover:bg-black/40',

  // Text on dark backgrounds
  'text-on-dark': 'text-white',

  // Admin interface colors
  'admin-accent': 'text-blue-600',
  'admin-accent-hover': 'hover:text-blue-700',
  'admin-accent-bg': 'bg-blue-50',
  'admin-accent-bg-hover': 'hover:bg-blue-100',
  'admin-border-hover': 'hover:border-blue-200',

  // Status indicator colors for badges/dots
  'status-indicator-open': 'bg-green-400',
  'status-indicator-closed': 'bg-red-400',

  // Featured badge gradients
  'gradient-featured-award': 'bg-gradient-to-r from-yellow-400 to-amber-500',
  'gradient-featured-award-text': 'text-white',

  // Hero section gradients
  'gradient-hero-overlay': 'bg-gradient-to-b from-white/80 via-transparent to-white/80',
  'gradient-image-overlay': 'bg-gradient-to-t from-black/60 via-black/20 to-transparent',
  'overlay-dark-60': 'bg-black/60',

  // CTA gradients
  'gradient-cta-blue': 'bg-gradient-to-br from-blue-50/70 to-white/80',

  // Gradient patterns for badges and highlights
  'gradient-purple-pink': 'bg-gradient-to-br from-purple-50 to-pink-50',
  'gradient-purple-pink-border': 'border-purple-200/50',
  'gradient-purple-pink-text': 'text-purple-700',
  'gradient-blue-cyan': 'bg-gradient-to-br from-blue-50 to-cyan-50',
  'gradient-blue-cyan-border': 'border-blue-200/50',
  'gradient-blue-cyan-text': 'text-blue-700',

  // Additional color tokens for common hardcoded classes
  // Red variants
  'red-600': 'bg-red-600',
  'red-600-bg': 'bg-red-600',
  'red-600-text': 'text-red-600',
  'red-700': 'text-red-700',
  'red-800': 'text-red-800',
  'red-50-bg': 'bg-red-50',
  'red-50-bg-hover': 'hover:bg-red-50',
  'red-100-bg': 'bg-red-100',
  'red-500': 'text-red-500',
  'red-400': 'text-red-400',
  'red-200': 'border-red-200',
  'red-200-border': 'border-red-200',

  // Purple variants
  'purple-900': 'text-purple-900',
  'purple-700': 'text-purple-700',
  'purple-600': 'text-purple-600',
  'purple-600-bg': 'bg-purple-600',
  'purple-600-border': 'border-purple-600',
  'purple-200': 'border-purple-200',
  'purple-200-border': 'border-purple-200',
  'purple-100-bg': 'bg-purple-100',
  'purple-50': 'bg-purple-50',
  'purple-50-bg': 'bg-purple-50',

  // Indigo variants
  'indigo-600': 'text-indigo-600',
  'indigo-600-text': 'text-indigo-600',
  'indigo-600-bg': 'bg-indigo-600',
  'indigo-500': 'text-indigo-500',
  'indigo-700': 'text-indigo-700',
  'indigo-200-border': 'border-indigo-200',
  'indigo-50-bg': 'bg-indigo-50',
  'indigo-50-bg-hover': 'hover:bg-indigo-50',
  'indigo-100-bg': 'bg-indigo-100',
  'indigo-100-border': 'border-indigo-100',

  // Blue variants
  'blue-900': 'text-blue-900',
  'blue-800': 'text-blue-800',
  'blue-700': 'text-blue-700',
  'blue-600': 'text-blue-600',
  'blue-500': 'text-blue-500',
  'blue-700-bg': 'bg-blue-700',
  'blue-600-bg': 'bg-blue-600',
  'blue-400': 'bg-blue-400',
  'blue-400-border': 'border-blue-400',
  'blue-200': 'border-blue-200',
  'blue-200-border': 'border-blue-200',
  'blue-300-border': 'border-blue-300',
  'blue-500-border': 'border-blue-500',
  'blue-600-border': 'border-blue-600',
  'blue-100': 'text-blue-100',
  'blue-100-text': 'text-blue-100',
  'blue-100-bg': 'bg-blue-100',
  'blue-100-bg-hover': 'hover:bg-blue-100',
  'blue-100-border': 'border-blue-100',
  'blue-50': 'bg-blue-50',
  'blue-50-bg': 'bg-blue-50',
  'blue-50-bg-hover': 'hover:bg-blue-50',

  // Green variants
  'green-900': 'text-green-900',
  'green-800': 'text-green-800',
  'green-700': 'text-green-700',
  'green-600': 'text-green-600',
  'green-600-text': 'text-green-600',
  'green-600-bg': 'bg-green-600',
  'green-500': 'text-green-500',
  'green-700-bg': 'bg-green-700',
  'green-400': 'text-green-400',
  'green-100-bg': 'bg-green-100',
  'green-50': 'bg-green-50',
  'green-50-bg': 'bg-green-50',
  'green-200': 'border-green-200',
  'green-200-border': 'border-green-200',
  'green-400-border': 'border-green-400',

  // Gray variants
  'gray-900': 'bg-gray-900',
  'gray-800': 'text-gray-800',
  'gray-700': 'text-gray-700',
  'gray-600': 'text-gray-600',
  'gray-500-border': 'border-gray-500',
  'gray-400': 'text-gray-400',
  'gray-300': 'text-gray-300',
  'gray-200': 'text-gray-200',
  'gray-200-bg': 'bg-gray-200',
  'gray-100': 'bg-gray-100',
  'gray-50': 'bg-gray-50',

  // Orange variants
  'orange-600': 'text-orange-600',
  'orange-600-bg': 'bg-orange-600',
  'orange-500': 'text-orange-500',
  'orange-400': 'text-orange-400',
  'orange-700': 'text-orange-700',
  'orange-700-bg': 'bg-orange-700',

  // Yellow variants
  'yellow-800': 'text-yellow-800',
  'yellow-600': 'text-yellow-600',
  'yellow-400': 'text-yellow-400',
  'yellow-200': 'border-yellow-200',
  'yellow-200-border': 'border-yellow-200',
  'yellow-100': 'bg-yellow-100',
  'yellow-50': 'bg-yellow-50',
  'yellow-50-bg': 'bg-yellow-50',

  // Amber variants
  'amber-50': 'bg-amber-50',
  'amber-50-bg': 'bg-amber-50',
  'amber-100': 'bg-amber-100',
  'amber-200': 'border-amber-200',
  'amber-200-border': 'border-amber-200',
  'amber-500-bg': 'bg-amber-500',
  'amber-500-text': 'text-amber-500',
  'amber-600': 'bg-amber-600',
  'amber-600-bg': 'bg-amber-600',
  'amber-600-text': 'text-amber-600',
  'amber-700': 'text-amber-700',
  'amber-800-text': 'text-amber-800',
  'amber-900': 'text-amber-900',
  'amber-100-border': 'border-amber-100',

  // Additional common colors
  'slate-900': 'text-slate-900',
  'slate-700': 'text-slate-700',
  'slate-600': 'text-slate-600',
  'slate-500': 'text-slate-500',
  'slate-400': 'text-slate-400',
  'slate-300': 'text-slate-300',
  'slate-300-border': 'border-slate-300',
  'slate-200': 'border-slate-200',
  'slate-100': 'bg-slate-100',
  'slate-100-bg': 'bg-slate-100',
  'slate-100-ring': 'ring-slate-100',
  'slate-200-ring': 'ring-slate-200',
  'slate-200-bg': 'bg-slate-200',
  'slate-200-border': 'border-slate-200',
  'slate-50': 'bg-slate-50',
  'slate-50-bg': 'bg-slate-50',
  'slate-25': 'bg-slate-25',
};

// Gradient tokens for background gradients
const GRADIENT_SCALE = {
  'to-br-white-transparent': 'bg-gradient-to-br from-white/80 via-white/60 to-white/30',
  'to-t-black-transparent': 'bg-gradient-to-t from-black/50 to-transparent',
  'to-br-blue-indigo': 'bg-gradient-to-br from-blue-500 to-indigo-600',
  'to-t-black-transparent-60': 'bg-gradient-to-t from-black/60 via-black/20 to-transparent',
  'to-t-black-transparent-70': 'bg-gradient-to-t from-black/70 via-black/20 to-transparent',
  'to-t-black-transparent-50': 'bg-gradient-to-t from-black/50 via-black/20 to-transparent',
  'to-br-gray-transparent': 'bg-gradient-to-br from-gray-50/80 via-white/70 to-white/40',
  'to-b-white-transparent': 'bg-gradient-to-b from-white via-gray-50/30 to-white',
  'to-r-transparent-gray': 'bg-gradient-to-r from-transparent via-gray-300 to-transparent',
  'to-r-transparent-gray-200': 'bg-gradient-to-r from-transparent via-gray-200 to-transparent',
  'to-r-gray-900': 'bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900',
  'to-r-transparent-white-blue': 'bg-gradient-to-r from-transparent via-white/70 to-transparent',
  'to-r-transparent-white-70': 'bg-gradient-to-r from-transparent via-white/70 to-transparent',
  'to-r-transparent-blue-200-60':
    'bg-gradient-to-r from-transparent via-blue-200/60 to-transparent',
  'to-br-white-transparent-40': 'bg-gradient-to-br from-white/40 via-transparent to-transparent',
  'to-br-white-transparent-35': 'bg-gradient-to-br from-white via-white/75 to-white/35',
  'to-br-white-via-70-to-35': 'bg-gradient-to-br from-white via-white/70 to-white/35',
  'to-br-white-via-65-to-35': 'bg-gradient-to-br from-white via-white/65 to-white/35',
  'to-br-white-transparent-35-opacity90':
    'bg-gradient-to-br from-white via-white/70 to-white/35 opacity-90',
  'to-t-black-transparent-70-20': 'bg-gradient-to-t from-black/70 via-black/20 to-transparent',
  'to-br-white-white-transparent-40': 'bg-gradient-to-br from-white via-white/70 to-white/40',
  'to-br-white-blue-transparent': 'bg-gradient-to-br from-white via-white/92 to-blue-50/60',
  'to-r-transparent-blue': 'bg-gradient-to-r from-transparent via-blue-200/60 to-transparent',
  'to-br-yellow-amber': 'bg-gradient-to-br from-yellow-50/80 via-amber-50/60 to-yellow-100/70',
  'to-br-gray-gradient': 'bg-gradient-to-br from-gray-400 to-gray-500',
  'to-br-gray-50-to-100': 'bg-gradient-to-br from-gray-50 to-gray-100',
  'to-br-white-90-to-gray-50-90': 'bg-gradient-to-br from-white/90 to-gray-50/90',
  'to-t-blue-50-transparent-70': 'bg-gradient-to-t from-blue-50 via-blue-50/70 to-transparent',
  'to-r-gray-transparent': 'bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900',
  'to-br-transparent-white': 'bg-gradient-to-br from-transparent via-white/10 to-transparent',
  'to-tl-transparent-white': 'bg-gradient-to-tl from-transparent via-white/5 to-transparent',
  'to-b-white-transparent-5-10': 'bg-gradient-to-b from-white/5 via-white/10 to-transparent',
  'via-blue-50-70': 'via-blue-50/70',
  'to-br-gray-white-transparent': 'bg-gradient-to-br from-gray-50/80 via-white/70 to-white/40',
  'to-b-white-gray-transparent': 'bg-gradient-to-b from-white via-gray-50/30 to-white',
  'to-b-gray-white-transparent': 'bg-gradient-to-b from-gray-50/30 via-white to-white',
  'to-r-gray-gradient': 'bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900',
  'to-r-blue-purple': 'bg-gradient-to-r from-blue-50 to-purple-50',
};

// Background gradient tokens used by components
const BACKGROUND_GRADIENT = {
  toRight: 'bg-gradient-to-r',
  toBottomRight: 'bg-gradient-to-br',
  fromWhite80: 'from-white/80',
  viaWhite60: 'via-white/60',
  toWhite30: 'to-white/30',
  fromTransparent: 'from-transparent',
  viaWhite70: 'via-white/70',
  toTransparent: 'to-transparent',
  fromGray900: 'from-gray-900',
  viaGray800: 'via-gray-800',
  toGray900: 'to-gray-900',
  viaBlue200: 'via-blue-200',
  fromBlue50: 'from-blue-50',
  fromYellow50: 'from-yellow-50',
  fromGreen50: 'from-green-50',
  toPurple50: 'to-purple-50',
  toAmber50: 'to-amber-50',
  toEmerald50: 'to-emerald-50',
  toIndigo50: 'to-indigo-50',
};

// Opacity tokens for common opacity values
const OPACITY_SCALE = {
  10: 'opacity-10',
  50: 'opacity-50',
  60: 'opacity-60',
  70: 'opacity-70',
  80: 'opacity-80',
  90: 'opacity-90',
  95: 'opacity-95',
};

const BACKGROUND_SIZE = {
  '32px_32px': 'bg-[size:32px_32px]',
};

const BACKDROP_BLUR = {
  sm: 'backdrop-blur-sm',
};

// Semantic typography patterns - common text combinations used across components
const TYPOGRAPHY_PATTERNS = {
  // Heading patterns
  heading: {
    h1: `${TYPE_SCALE['5xl']} ${FONT_WEIGHTS.bold} ${TYPE_LEADING.tight} ${COLOR_SCALE.foreground} -tracking-[0.02em]`,
    h2: `${TYPE_SCALE['4xl']} ${FONT_WEIGHTS.bold} ${TYPE_LEADING.tight} ${COLOR_SCALE.foreground} -tracking-[0.015em]`,
    h3: `${TYPE_SCALE.xl} ${FONT_WEIGHTS.semibold} ${TYPE_LEADING.tight} ${COLOR_SCALE.foreground} -tracking-[0.01em]`,
    h4: `${TYPE_SCALE.lg} ${FONT_WEIGHTS.semibold} ${TYPE_LEADING.normal} ${COLOR_SCALE.foreground} -tracking-[0.01em]`,
  },

  // Body text patterns
  body: {
    large: `${TYPE_SCALE.lg} ${FONT_WEIGHTS.regular} ${TYPE_LEADING.relaxed} ${COLOR_SCALE.foreground}`,
    base: `${TYPE_SCALE.base} ${FONT_WEIGHTS.regular} ${TYPE_LEADING.relaxed} ${COLOR_SCALE.foreground}`,
    small: `${TYPE_SCALE.sm} ${FONT_WEIGHTS.regular} ${TYPE_LEADING.normal} ${COLOR_SCALE.foreground}`,
    caption: `${TYPE_SCALE.xs} ${FONT_WEIGHTS.regular} ${TYPE_LEADING.normal} ${COLOR_SCALE['foreground-muted']}`,
  },

  // Interactive text patterns
  interactive: {
    link: `${TYPE_SCALE.base} ${FONT_WEIGHTS.medium} ${TYPE_LEADING.normal} ${COLOR_SCALE['interactive-primary']}`,
    button: `${TYPE_SCALE.base} ${FONT_WEIGHTS.semibold} ${TYPE_LEADING.normal} tracking-tight`,
    label: `${TYPE_SCALE.sm} ${FONT_WEIGHTS.medium} ${TYPE_LEADING.normal} ${COLOR_SCALE.foreground}`,
  },
};

// Export layout and skeleton patterns separately
export const layout = LAYOUT_TOKENS.layout;
export const skeleton = LAYOUT_TOKENS.skeleton;

// Underscore-prefixed exports for backward compatibility
export const _INTERACTIVE_STATES = INTERACTIVE_STATES;
export const _MARGIN_LEFT = MARGIN_LEFT;
export const _MARGIN_TOP = MARGIN_TOP;
export const _MARGIN_BOTTOM = MARGIN_BOTTOM;
export const _FLEX_UTILITIES = FLEX_UTILITIES;
export const _MARGIN_X = MARGIN_X;
export const _OVERFLOW_Y = OVERFLOW_Y;
export const _BORDER_WIDTH = BORDER_WIDTH;
export const _SIZE_SCALE = SIZE_SCALE;
export const _SPACE_Y_SCALE = SPACE_Y_SCALE;
export const _LAYOUT_TOKENS = LAYOUT_TOKENS;
export const _PADDING_TOP = PADDING_TOP;
export const _PADDING_BOTTOM = PADDING_BOTTOM;
export const _SHADOW_SCALE = SHADOW_SCALE;
export const _DIVIDE = DIVIDE;

// Pointer events utilities
const POINTER_EVENTS_MAP = {
  'pointer-events-none': 'pointer-events-none',
  'pointer-events-auto': 'pointer-events-auto',
  none: 'pointer-events-none',
  auto: 'pointer-events-auto',
};

// Flex utilities
const FLEX_MAP = {
  'flex-shrink-0': 'FLEX_SHRINK[0]',
  'order-1': 'order: 1',
  'md:order-2': 'md:order-2',
  'justify-self-center': 'justify-self: center',
  'justify-self-end': 'justify-self: end',
  'ml-auto': 'MARGIN_LEFT.auto',
};

// Background utilities map
const BACKGROUND_MAP = {
  'bg-gradient-to-br': 'bg-gradient-to-br',
  'bg-gradient-to-r': 'bg-gradient-to-r',
  'bg-gradient-to-t': 'bg-gradient-to-t',
  'from-gray-200': 'from-gray-200',
  'to-gray-300': 'to-gray-300',
  'from-gray-50': 'from-gray-50',
  'to-gray-100': 'to-gray-100',
  'from-white': 'from-white',
  'to-white': 'to-white',
  'from-slate-50': 'from-slate-50',
  'to-slate-100': 'to-slate-100',
  'from-slate-900': 'from-slate-900',
  'via-slate-800': 'via-slate-800',
  'to-slate-700': 'to-slate-700',
  'via-white': 'via-white',
  'from-transparent': 'from-transparent',
  'to-transparent': 'to-transparent',
  'via-gray-200': 'via-gray-200',
  'via-gray-300': 'via-gray-300',
  'bg-black/60': 'bg-black/60',
  'bg-black/80': 'bg-black/80',
  'bg-black/90': 'bg-black/90',
  'group-hover:from-white': 'group-hover:from-white',
  'group-hover:via-white/75': 'group-hover:via-white/75',
  'group-hover:to-white/40': 'group-hover:to-white/40',
};

// Missing tokens that components are expecting
export const _CURSOR = {
  pointer: 'cursor-pointer',
  default: 'cursor-default',
  notAllowed: 'cursor-not-allowed',
};

export const _USER_SELECT = {
  none: 'select-none',
  text: 'select-text',
  all: 'select-all',
  auto: 'select-auto',
};

export const _INLINE_FLEX = {
  inlineFlex: 'inline-flex',
  inlineFlexCol: 'inline-flex flex-col',
  inlineFlexRow: 'inline-flex flex-row',
};

export const MARGIN_AUTO = 'mx-auto';

export {
  GAP_SCALE,
  GAP_X_SCALE,
  GAP_Y_SCALE,
  FLEX_UTILITIES as FLEX,
  FLEX_UTILITIES,
  FLEX_ALIGN_SCALE as FLEX_ALIGN,
  FLEX_JUSTIFY_SCALE as FLEX_JUSTIFY,
  CONTROL_SIZES,
  CONTROL_ICON_SIZES,
  RADIUS_SCALE,
  SURFACE_PADDING,
  SPACING,
  INTERACTION,
  SURFACE_CONTENT_TOP_PADDING,
  SURFACE_HEADER_PADDING,
  SURFACE_FOOTER_PADDING,
  CONTAINER_WIDTHS,
  CONTAINER_PADDING,
  SECTION_VERTICAL_PADDING,
  SECTION_BACKGROUNDS,
  GRID_BASE_COLUMNS,
  GRID_RESPONSIVE_COLUMNS,
  GRID_TEMPLATE_COLUMNS,
  GRID_AUTO_ROWS,
  TYPE_SCALE,
  TYPE_LEADING,
  FONT_WEIGHTS,
  TEXT_ALIGNMENT,
  TEXT_ALIGNMENT as TEXT,
  PROSE,
  WORD_BREAK,
  LETTER_SPACING,
  WHITESPACE,
  RESPONSIVE_WHITESPACE,
  DISPLAY,
  RESPONSIVE_DISPLAY,
  RESPONSIVE_POSITION_SCALE,
  RESPONSIVE_GRID,
  RESPONSIVE_COL_SPAN,
  TEXT_TRANSFORM,
  TEXT_STYLING,
  TEXT_DECORATION,
  LINE_HEIGHT,
  LINE_CLAMP,
  TYPOGRAPHY_PATTERNS,
  ANIMATIONS,
  Z_INDEX,
  INTERACTIVE_STATES,
  SIZE_SCALE,
  MIN_WIDTH_SCALE,
  MAX_WIDTH_SCALE,
  RESPONSIVE_SIZE_SCALE,
  COLOR_SCALE,
  GRADIENT_SCALE,
  BACKGROUND_GRADIENT,
  OPACITY_SCALE,
  BACKGROUND_SIZE,
  BACKDROP_BLUR,
  PADDING_X,
  PADDING_Y,
  PADDING_TOP,
  PADDING_BOTTOM,
  PADDING_RIGHT,
  PADDING_LEFT,
  MARGIN_X,
  MARGIN_Y,
  MARGIN_LEFT,
  MARGIN_RIGHT,
  MARGIN_TOP,
  SCROLL_MARGIN_TOP,
  RESPONSIVE_FIRST_MARGIN_TOP,
  MARGIN_BOTTOM,
  NEGATIVE_MARGIN_X,
  NEGATIVE_MARGIN_Y,
  NEGATIVE_MARGIN_TOP,
  NEGATIVE_MARGIN_RIGHT,
  NEGATIVE_MARGIN_BOTTOM,
  SHADOW_SCALE,
  BORDER,
  BORDER_WIDTH,
  BORDER_STYLE,
  BORDER_TOP,
  BORDER_LEFT,
  BORDER_COLOR,
  BACKGROUND_COLOR,
  OPACITY,
  HOVER_OPACITY,
  DIVIDE,
  POSITION_SCALE,
  SPACE_SCALE,
  SPACE_X_SCALE,
  SPACE_Y_SCALE,
  LAYOUT_TOKENS,
  TRANSFORM_SCALE,
  OVERFLOW,
  OVERFLOW_Y,
  FLEX_SHRINK,
  FLEX_GROW,
  FLEX_BASIS,
  FLEX_WRAP,
  FLEX_SELF,
  INTERACTIVE_HEIGHTS,
  _CURSOR as CURSOR,
  _USER_SELECT as USER_SELECT,
  ASPECT_RATIO,
  BLUR_SCALE,
  DURATION_SCALE,
  FOCUS_SCALE,
  ACCESSIBILITY_SCALE,
  OBJECT_FIT_SCALE,
  PRINT_SCALE,
  GRID_JUSTIFY_SELF,
  CUSTOM_CLASSES,
  RESPONSIVE_TYPE_SCALE,
  RESPONSIVE_GAP_SCALE,
  POINTER_EVENTS_MAP,
  FLEX_MAP,
  BACKGROUND_MAP,
  componentMaxHeightMd,
  componentMaxHeightLg,
};

// Export TRANSITION as an alias for INTERACTIVE_STATES.transitions for backward compatibility
export const TRANSITION = INTERACTIVE_STATES.transitions;

// Export POSITION as an alias for POSITION_SCALE for backward compatibility
export const POSITION = POSITION_SCALE;

// Export convenience tokens for common transforms
export const TRANSFORM = {
  translateXPx: TRANSFORM_SCALE['translate-x'].px,
};

// Accessibility utilities for screen readers and keyboard navigation
const ACCESSIBILITY_SCALE = {
  srOnly: 'sr-only',
  notSrOnly: 'not-sr-only',
  skipToMain: 'sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2',
};

// Focus state mappings for accessibility and keyboard navigation
const FOCUS_SCALE = {
  outline: 'focus:outline-none',
  'ring-blue': 'focus:ring-2 focus:ring-blue-500/20 focus:ring-offset-1',
  'ring-blue-offset': 'focus:ring-2 focus:ring-blue-500/20 focus:ring-offset-2',
  'ring-emerald': 'focus:ring-2 focus:ring-emerald-500/20 focus:ring-offset-1',
  'ring-emerald-offset': 'focus:ring-2 focus:ring-emerald-500/20 focus:ring-offset-2',
  'ring-indigo': 'focus:ring-2 focus:ring-indigo-500/20 focus:ring-offset-1',
  'ring-indigo-offset': 'focus:ring-2 focus:ring-indigo-500/20 focus:ring-offset-2',
  'visible-ring-blue':
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
  'visible-ring-blue-white':
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white',
};

// Object fit utilities for image positioning
const OBJECT_FIT_SCALE = {
  contain: 'object-contain',
  cover: 'object-cover',
  fill: 'object-fill',
  none: 'object-none',
  'scale-down': 'object-scale-down',
};

// Print utilities for controlling display in print media
const PRINT_SCALE = {
  hidden: 'print:hidden',
  block: 'print:block',
  none: 'no-print', // Custom utility class
};

// Custom CSS classes that are defined in external stylesheets
const CUSTOM_CLASSES = {
  'hero-pattern-layer': 'hero-pattern-layer',
  'provider-dot': 'provider-dot',
  'provider-dot-circle': 'provider-dot-circle',
  'provider-dot-tooltip': 'provider-dot-tooltip',
  'provider-dot-thumbnail': 'provider-dot-thumbnail',
  'provider-dot-featured': 'provider-dot-featured',
  'toc-container': 'toc-container',
  'no-image-text': 'no-image-text',
  'descendant-text-white-opacity-90': '[&_*]:text-white [&_*]:opacity-90',
  'shimmer-effect': 'shimmer-effect',
};
